import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPanelTitle,
  AdminStatusBadge,
  AdminTableBody,
  AdminSupportingText,
  AdminAlertText,
  AdminRowAction,
  AdminHeaderAction,
  AdminLinkAction,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminTabButton,
  AdminFlagStatusBadge,
  AdminProfileAvatar,
  AdminDetailTableHeader,
  AdminFormSecondaryAction,
  ADMIN_TAB_CONTENT_CONTAINER_CLASS,
  ADMIN_TAB_CONTENT_OFFSET_CLASS,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
} from '../shared/adminUx';

import Profile from "../../../assets/img/User.png";
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import useClickOutside from '../../../Hooks/useClickOutside';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import { getBusinessesForScenarios } from '../../../data/postModerationScenarios';
import { getBusinessesForProfileScenarios, getBusinessScenarioByBusinessId } from '../../../data/profileModerationScenarios';
import { getBusinessesFromReviewScenarios } from '../../../data/reviewModerationScenarios';
import {
  getAccountAuditLogRows,
  recordAccountActivity,
  syncLocalAuditLogsToChangeHistory,
} from '../../../data/changeHistoryAudit';

const mapCentralAuditToLocal = (rows) =>
  (rows || []).map((row) => ({
    id: row.changeId || row.id,
    timestamp: row.dateTime || row.timestamp,
    performedBy: row.performedBy,
    action: row.event || row.action,
    summary: row.summary,
    details: row.details,
    relatedRecord: row.relatedRecord || row.record || '—',
  }));

const refreshBusinessAuditLogs = (accountRef, fallbackLogs = []) => {
  if (!accountRef) return fallbackLogs;
  syncLocalAuditLogsToChangeHistory({
    logs: fallbackLogs,
    module: 'Businesses',
    sourceModuleRef: 'businesses',
    sourceItemRef: accountRef,
    accountRef,
    changedItem: accountRef,
    itemId: accountRef,
  });
  return mapCentralAuditToLocal(getAccountAuditLogRows(accountRef));
};

const BusinessSecurityConfirmModal = ({ title, message, confirmLabel, onConfirm, onClose }) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
    onClick={onClose}
    role="presentation"
  >
    <div
      className={`bg-white rounded-xl shadow-lg ${ACTION_POPUP_CONTENT_WIDTH_CLASS}`}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <div className="px-5 pt-5 pb-3 border-b border-lightgray">
        <AdminPanelTitle className="text-offblack text-center">{title}</AdminPanelTitle>
      </div>
      <div className="px-5 py-4">
        <AdminSupportingText className="text-offblack text-center">{message}</AdminSupportingText>
      </div>
      <div className="px-5 pb-5 flex flex-wrap justify-end gap-3">
        <AdminFormSecondaryAction type="button" onClick={onClose}>
          Cancel
        </AdminFormSecondaryAction>
        <AdminHeaderAction type="button" onClick={onConfirm}>
          {confirmLabel}
        </AdminHeaderAction>
      </div>
    </div>
  </div>
);

function countOpenFlagsForOverview(business) {
  if (business?.overviewOpenFlagsCount != null) return Number(business.overviewOpenFlagsCount);
  const list =
    business?.moderationAndFlags && business.moderationAndFlags.length > 0
      ? business.moderationAndFlags
      : business?.flags || [];
  if (!list.length) return 0;
  return list.filter((f) => {
    const st = String(f.status || '').toLowerCase().trim();
    return (
      st === 'new' ||
      st === 'active' ||
      st === 'open' ||
      st === 'under review' ||
      st === 'escalated' ||
      st === 'attached'
    );
  }).length;
}

/** Account Status: Active, Inactive, Restricted, Banned, Closed. */
function formatAccountStatusOverview(businessOrStatus) {
  const status =
    businessOrStatus != null && typeof businessOrStatus === 'object'
      ? businessOrStatus.status ?? businessOrStatus.accountStatus
      : businessOrStatus;
  const raw = String(status || '').trim();
  if (!raw) return 'Active';
  const lower = raw.toLowerCase();
  if (lower === 'active') return 'Active';
  if (lower === 'inactive' || lower === 'deleted') return 'Inactive';
  if (lower === 'restricted' || lower === 'under review' || lower === 'suspended') return 'Restricted';
  if (lower === 'banned' || lower === 'blocked') return 'Banned';
  if (lower === 'closed') return 'Closed';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/** Verification Status: Pending or Verified. */
function formatVerificationStatusOverview(business) {
  const raw = String(business?.verificationStatus || '').trim();
  if (raw) {
    const lower = raw.toLowerCase();
    if (lower === 'verified') return 'Verified';
    if (lower === 'pending') return 'Pending';
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }
  if (business?.emailVerified && business?.abnVerified) return 'Verified';
  return 'Pending';
}

function formatBusinessIdDisplay(business) {
  const id = business?.businessId || business?.id;
  if (!id) return '';
  if (typeof id === 'string' && id.startsWith('BUS-')) return id;
  const numericPart = String(id).replace(/\D/g, '');
  return numericPart ? `BUS-${numericPart.padStart(5, '0')}` : String(id);
}

function formatPlanOverview(business) {
  const raw = business?.subscription?.plan ?? business?.plan ?? business?.planType;
  if (raw == null || raw === '') return '—';
  const p = String(raw).trim();
  if (p.toUpperCase() === 'PRO') return 'Pro';
  if (p.toLowerCase() === 'basic') return 'Basic';
  return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
}

/** Flag sources: User Flag or System Flag (Team Flags / Compliance Flags are not supported). */
function mapFlagSourceToSpec(raw, ctx = {}) {
  const from = String(raw || ctx.source || '').trim();
  const type = String(ctx.type || ctx.category || ctx.contentType || '').toLowerCase();
  const lowered = from.toLowerCase();

  if (
    lowered === 'user flag' ||
    lowered === 'customer' ||
    lowered === 'business' ||
    lowered.includes('user report') ||
    lowered.includes('user flag')
  ) {
    return 'User Flag';
  }

  if (
    lowered === 'system flag' ||
    lowered === 'system' ||
    lowered === 'auto' ||
    type.includes('auto-flag') ||
    type.includes('system flag')
  ) {
    return 'System Flag';
  }

  // Message / Quote threads are recipient User Flags only (not System-scanned)
  if (
    type.includes('message') ||
    type.includes('quote') ||
    ctx.messageId ||
    ctx.quoteId
  ) {
    return 'User Flag';
  }

  return 'System Flag';
}

function normalizeFlagReasonText(item) {
  const raw = item.reason ?? item.trigger ?? item.object;
  if (raw == null) return '—';
  if (typeof raw === 'object' && raw !== null) {
    if (raw.title) return String(raw.title).trim() || '—';
    return '—';
  }
  const s = String(raw).trim();
  return s || '—';
}

function normalizeFlagSeverity(value) {
  let s = String(value || 'Medium').trim();
  if (s === 'Critical') s = 'High';
  if (!['Low', 'Medium', 'High'].includes(s)) s = 'Medium';
  return s;
}

function normalizeFlagStatusSpec(status) {
  const v = String(status || 'New').trim();
  const lower = v.toLowerCase();
  if (['decided', 'resolved', 'approved', 'closed'].includes(lower)) return 'Decided';
  if (['active', 'under review', 'escalated', 'attached'].includes(lower)) return 'Active';
  if (['new', 'open'].includes(lower)) return 'New';
  if (v === 'New' || v === 'Active' || v === 'Decided') return v;
  return 'New';
}

function normalizeBusinessFlagId(id, index = 0) {
  if (!id) return `FLG-${String(10000 + index).padStart(5, '0')}`;
  if (typeof id === 'string' && id.startsWith('FLG-')) return id;
  const match = id.toString().match(/(\d+)/);
  if (match) return `FLG-${String(match[1]).padStart(5, '0')}`;
  const hash = id.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return `FLG-${String((hash % 99999) + 10000).padStart(5, '0')}`;
}

function normalizeModerationCaseId(id) {
  if (!id) return '';
  const raw = String(id).trim();
  if (/^MOA-/i.test(raw)) return raw.toUpperCase();
  if (/^(?:MODS|MOD)-/i.test(raw)) {
    const digits = raw.replace(/^(?:MODS|MOD)-?/i, '').replace(/\D/g, '');
    if (digits) return `MOD-${digits.padStart(6, '0')}`;
    return raw.toUpperCase();
  }
  const digits = raw.replace(/\D/g, '');
  if (digits) return `MOD-${digits.padStart(6, '0')}`;
  return raw;
}

/** Resolve case ID + Open Case action for a business flag row. */
function resolveBusinessFlagHandling(flag) {
  const linkedCaseId = flag.linkedCaseId || flag.caseId || null;
  const handlingStatus = normalizeFlagStatusSpec(flag.handlingStatus || flag.status);
  const caseId = normalizeModerationCaseId(linkedCaseId) || '—';
  const isDecided = handlingStatus === 'Decided';

  return {
    handlingRecord: caseId,
    handlingRecordId: caseId !== '—' ? caseId : null,
    handlingStatus,
    actionLabel: caseId !== '—' ? 'Open Case' : null,
    actionPath:
      caseId !== '—'
        ? `/admin/moderation/queue/${encodeURIComponent(caseId)}`
        : null,
    openFromArchive: isDecided,
  };
}

/** Flag ID | Case ID | Source | Reason | Severity | Case Status | Created On | Last Updated | Action */
const FLAGS_TABLE_GRID =
  'minmax(0, 0.7fr) minmax(0, 0.9fr) minmax(0, 0.85fr) minmax(0, 1.2fr) minmax(0, 0.5fr) minmax(0, 0.85fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)';

/** Date and Time | Event | Details | Related Record */
const AUDIT_LOG_TABLE_GRID =
  'minmax(0, 1.1fr) minmax(0, 1.2fr) minmax(0, 2fr) minmax(0, 0.9fr)';

const BusinessDetail = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [businessIdCopied, setBusinessIdCopied] = useState(false);
  const [copiedContactField, setCopiedContactField] = useState(null);
  const [securityConfirm, setSecurityConfirm] = useState(null);
  
  // Dropdown states for logs filters
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [performedByOpen, setPerformedByOpen] = useState(false);
  const [moduleOpen, setModuleOpen] = useState(false);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  
  // Filter values
  const [filters, setFilters] = useState({
    dateRange: 'All',
    performedBy: 'All',
    module: 'All',
    outcome: 'All'
  });
  
  // Flags filters
  const [flagsFilters, setFlagsFilters] = useState({
    source: [], // Multi-select: System, Customer, Business, Admin
    category: [], // Multi-select: Compliance, Moderation, Fraud, Security, System
    reason: [], // Multi-select: Compliance breach, Scam, Abuse, Spam, Policy breach, Other
    severity: [], // Multi-select: Low, Medium, High, Critical
    status: [], // Multi-select: Open, Under Review, Closed
    dateFrom: '',
    dateTo: '',
    hasLinkedCase: null // null (All), true (Has linked case), false (No linked case)
  });
  
  // Flags filter dropdown states
  const [flagsSourceFilterOpen, setFlagsSourceFilterOpen] = useState(false);
  const [flagsCategoryFilterOpen, setFlagsCategoryFilterOpen] = useState(false);
  const [flagsReasonFilterOpen, setFlagsReasonFilterOpen] = useState(false);
  const [flagsSeverityFilterOpen, setFlagsSeverityFilterOpen] = useState(false);
  const [flagsStatusFilterOpen, setFlagsStatusFilterOpen] = useState(false);
  const [flagsDateRangeFilterOpen, setFlagsDateRangeFilterOpen] = useState(false);
  const [flagsLinkedCaseFilterOpen, setFlagsLinkedCaseFilterOpen] = useState(false);
  
  // Flags table pagination
  const [flagsCurrentPage, setFlagsCurrentPage] = useState(1);
  useEffect(() => {
    setFlagsCurrentPage(1);
  }, [businessId]);
  
  // Refs for flags filters
  const flagsSourceFilterRef = useRef(null);
  const flagsCategoryFilterRef = useRef(null);
  const flagsReasonFilterRef = useRef(null);
  const flagsSeverityFilterRef = useRef(null);
  const flagsStatusFilterRef = useRef(null);
  const flagsDateRangeFilterRef = useRef(null);
  const flagsLinkedCaseFilterRef = useRef(null);
  
  // Click outside handlers for flags filters
  useClickOutside(flagsSourceFilterRef, () => setFlagsSourceFilterOpen(false));
  useClickOutside(flagsCategoryFilterRef, () => setFlagsCategoryFilterOpen(false));
  useClickOutside(flagsReasonFilterRef, () => setFlagsReasonFilterOpen(false));
  useClickOutside(flagsSeverityFilterRef, () => setFlagsSeverityFilterOpen(false));
  useClickOutside(flagsStatusFilterRef, () => setFlagsStatusFilterOpen(false));
  useClickOutside(flagsDateRangeFilterRef, () => setFlagsDateRangeFilterOpen(false));
  useClickOutside(flagsLinkedCaseFilterRef, () => setFlagsLinkedCaseFilterOpen(false));
  
  // Refs for click outside (moderation code removed - no longer needed)
  const dateRangeRef = useRef(null);
  const performedByRef = useRef(null);
  const moduleRef = useRef(null);
  const outcomeRef = useRef(null);
  
  // Click outside handlers
  useClickOutside(dateRangeRef, () => setDateRangeOpen(false));
  useClickOutside(performedByRef, () => setPerformedByOpen(false));
  useClickOutside(moduleRef, () => setModuleOpen(false));
  useClickOutside(outcomeRef, () => setOutcomeOpen(false));

  // Handle clicking outside to close all filter dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside all filter dropdown containers
      const filterRefs = [dateRangeRef, performedByRef, moduleRef, outcomeRef];
      const isOutsideAll = filterRefs.every(ref => {
        const el = ref?.current;
        return !el || !el.contains(event.target);
      });

      if (isOutsideAll) {
        setDateRangeOpen(false);
        setPerformedByOpen(false);
        setModuleOpen(false);
        setOutcomeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);


  // Filter change handler
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Pagination states for Quotes, Messages & Activity
  const [quotesCurrentPage, setQuotesCurrentPage] = useState(1);
  const [messagesCurrentPage, setMessagesCurrentPage] = useState(1);
  const [activityCurrentPage, setActivityCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Messages & Quotes filters
  const [messagesFilters, setMessagesFilters] = useState({
    type: 'All', // All, Message, Direct Quote, Group Quote
    status: 'All', // All, New Quote, Quote Replied, etc.
    customer: 'All',
    dateFrom: '',
    dateTo: '',
    flagged: 'All' // All, Yes, No
  });
  
  // Customer search for filter
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');

  // Posts filters
  const [postsFilters, setPostsFilters] = useState({
    type: 'All', // All, Standard, Featured
    status: 'All', // All, Published, Hidden, Removed, Pending
    category: 'All',
    flagged: 'All', // All, Yes, No
    dateFrom: '',
    dateTo: ''
  });
  
  // Posts filter dropdown states
  const [postsTypeFilterOpen, setPostsTypeFilterOpen] = useState(false);
  const [postsStatusFilterOpen, setPostsStatusFilterOpen] = useState(false);
  const [postsCategoryFilterOpen, setPostsCategoryFilterOpen] = useState(false);
  const [postsFlaggedFilterOpen, setPostsFlaggedFilterOpen] = useState(false);
  const [postsDateRangeFilterOpen, setPostsDateRangeFilterOpen] = useState(false);
  
  // Row actions for posts table
  const [postsRowActionsOpen, setPostsRowActionsOpen] = useState({});
  const postsRowActionsRefs = useRef({});
  
  // Refs for posts filters
  const postsTypeFilterRef = useRef(null);
  const postsStatusFilterRef = useRef(null);
  const postsCategoryFilterRef = useRef(null);
  const postsFlaggedFilterRef = useRef(null);
  const postsDateRangeFilterRef = useRef(null);
  
  // Click outside handlers for posts filters
  useClickOutside(postsTypeFilterRef, () => setPostsTypeFilterOpen(false));
  useClickOutside(postsStatusFilterRef, () => setPostsStatusFilterOpen(false));
  useClickOutside(postsCategoryFilterRef, () => setPostsCategoryFilterOpen(false));
  useClickOutside(postsFlaggedFilterRef, () => setPostsFlaggedFilterOpen(false));
  useClickOutside(postsDateRangeFilterRef, () => setPostsDateRangeFilterOpen(false));
  
  // Toggle row actions for posts
  const togglePostsRowActions = (postId) => {
    setPostsRowActionsOpen((prev) => {
      if (prev[postId]) return {};
      return { [postId]: true };
    });
  };
  
  // Handle row action clicks for posts
  const handlePostsRowAction = (action, post) => {
    setPostsRowActionsOpen(prev => ({ ...prev, [post.id]: false }));
    
    switch (action) {
      case 'openPost':
        navigate(`/admin/posts/${post.id}`);
        break;
      case 'openModeration':
        navigate(`/admin/moderation/queue?postId=${post.id}`);
        break;
      default:
        break;
    }
  };
  
  // Click outside handler for posts row actions
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(postsRowActionsRefs.current).every(ref => {
        return ref && !ref.contains(event.target);
      });
      
      if (isOutsideAll) {
        setPostsRowActionsOpen({});
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Row actions for reviews table
  const [reviewsRowActionsOpen, setReviewsRowActionsOpen] = useState({});
  const reviewsRowActionsRefs = useRef({});
  
  // Toggle row actions for reviews
  const toggleReviewsRowActions = (reviewId) => {
    setReviewsRowActionsOpen((prev) => {
      if (prev[reviewId]) return {};
      return { [reviewId]: true };
    });
  };
  
  // Handle row action clicks for reviews
  const handleReviewsRowAction = (action, review) => {
    setReviewsRowActionsOpen(prev => ({ ...prev, [review.id]: false }));
    
    switch (action) {
      case 'openReview':
        navigate(`/admin/reviews/${review.id}`);
        break;
      case 'openCustomer':
        if (review.customerId) {
          navigate(`/admin/users/customers/${review.customerId}`);
        }
        break;
      case 'openModeration':
        navigate(`/admin/moderation/queue?reviewId=${review.id}`);
        break;
      default:
        break;
    }
  };
  
  // Click outside handler for reviews row actions
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(reviewsRowActionsRefs.current).every(ref => {
        return ref && !ref.contains(event.target);
      });
      
      if (isOutsideAll) {
        setReviewsRowActionsOpen({});
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Transactions filters
  const [transactionsFilters, setTransactionsFilters] = useState({
    type: 'All', // All, Subscription, Renewal, Adjustment, Refund
    status: 'All', // All, Paid, Pending, Failed, Refunded
    dateFrom: '',
    dateTo: '',
    amountFrom: '',
    amountTo: ''
  });
  
  // Transactions filter dropdown states
  const [transactionsTypeFilterOpen, setTransactionsTypeFilterOpen] = useState(false);
  const [transactionsStatusFilterOpen, setTransactionsStatusFilterOpen] = useState(false);
  const [transactionsDateRangeFilterOpen, setTransactionsDateRangeFilterOpen] = useState(false);
  const [transactionsAmountRangeFilterOpen, setTransactionsAmountRangeFilterOpen] = useState(false);
  
  // Refs for transactions filters
  const transactionsTypeFilterRef = useRef(null);
  const transactionsStatusFilterRef = useRef(null);
  const transactionsDateRangeFilterRef = useRef(null);
  const transactionsAmountRangeFilterRef = useRef(null);
  
  // Click outside handlers for transactions filters
  useClickOutside(transactionsTypeFilterRef, () => setTransactionsTypeFilterOpen(false));
  useClickOutside(transactionsStatusFilterRef, () => setTransactionsStatusFilterOpen(false));
  useClickOutside(transactionsDateRangeFilterRef, () => setTransactionsDateRangeFilterOpen(false));
  useClickOutside(transactionsAmountRangeFilterRef, () => setTransactionsAmountRangeFilterOpen(false));
  
  // Messages & Quotes filter dropdown states
  const [typeFilterOpen, setTypeFilterOpen] = useState(false);
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [customerFilterOpen, setCustomerFilterOpen] = useState(false);
  const [dateRangeFilterOpen, setDateRangeFilterOpen] = useState(false);
  const [flaggedFilterOpen, setFlaggedFilterOpen] = useState(false);
  
  // Row actions for messages/quotes table
  const [rowActionsOpen, setRowActionsOpen] = useState({});
  const rowActionsRefs = useRef({});
  
  // Refs for messages filters
  const typeFilterRef = useRef(null);
  const statusFilterRef = useRef(null);
  const customerFilterRef = useRef(null);
  const dateRangeFilterRef = useRef(null);
  const flaggedFilterRef = useRef(null);
  
  // Click outside handlers for messages filters
  useClickOutside(typeFilterRef, () => setTypeFilterOpen(false));
  useClickOutside(statusFilterRef, () => setStatusFilterOpen(false));
  useClickOutside(customerFilterRef, () => setCustomerFilterOpen(false));
  useClickOutside(dateRangeFilterRef, () => setDateRangeFilterOpen(false));
  useClickOutside(flaggedFilterRef, () => setFlaggedFilterOpen(false));
  
  // Toggle row actions for messages/quotes
  const toggleRowActions = (itemId) => {
    setRowActionsOpen((prev) => {
      if (prev[itemId]) return {};
      return { [itemId]: true };
    });
  };
  
  // Handle row action clicks for messages/quotes
  const handleRowAction = (action, item) => {
    setRowActionsOpen(prev => ({ ...prev, [item.id]: false }));
    
    switch (action) {
      case 'openThread':
        if (item.itemType === 'message') {
          navigate(`/admin/communication/all/${item.id}`);
        }
        break;
      case 'openQuote':
        if (item.itemType === 'directQuote') {
          navigate(`/admin/quotes/all/${item.id || item.quoteId}`);
        } else if (item.itemType === 'groupQuote') {
          navigate(`/admin/quotes/group/${item.id || item.quoteId}`);
        }
        break;
      case 'viewCustomer':
        if (item.customerId) {
          navigate(`/admin/users/customers/${item.customerId}`);
        }
        break;
      default:
        break;
    }
  };
  
  // Click outside handler for row actions
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(rowActionsRefs.current).every(ref => {
        return ref && !ref.contains(event.target);
      });
      
      if (isOutsideAll) {
        setRowActionsOpen({});
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Generate business detail data based on businessId
  useEffect(() => {
    const fetchBusinessDetail = async () => {
      setLoading(true);
      try {
        // TODO: Replace with actual API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const passedBusiness = location.state?.business;
        let foundBusiness = passedBusiness;
        if (!foundBusiness && businessId) {
          const all = [...getBusinessesForScenarios(), ...getBusinessesForProfileScenarios(), ...getBusinessesFromReviewScenarios()];
          const normalized = String(businessId).replace(/^BUS-?/i, '');
          foundBusiness = all.find((b) => String(b.businessId || b.id).replace(/^BUS-?/i, '') === normalized) || null;
        }
        const isProfileScenarioBusiness = foundBusiness?.scenarioKey != null || (businessId && getBusinessScenarioByBusinessId(businessId));
        const numericId = businessId ? parseInt(String(businessId).replace(/^BUS-?/i, ''), 10) || 1 : 1;
        if (!foundBusiness) {
          setBusiness(null);
          setLoading(false);
          return;
        }
        const now = new Date();
        const joinedDate = foundBusiness?.joined ? new Date(foundBusiness.joined) : new Date(now);
        if (!foundBusiness?.joined) {
          joinedDate.setDate(joinedDate.getDate() - Math.floor(Math.random() * 365));
        }
        const lastLoginDate = foundBusiness?.lastLogin ? new Date(foundBusiness.lastLogin) : new Date(now);
        if (!foundBusiness?.lastLogin) {
          lastLoginDate.setDate(lastLoginDate.getDate() - Math.floor(Math.random() * 30));
        }
        const passwordLastChangedDate = new Date(now);
        passwordLastChangedDate.setDate(passwordLastChangedDate.getDate() - Math.floor(Math.random() * 90));

        const businessData = {
          ...foundBusiness,
          businessId: foundBusiness?.businessId || foundBusiness?.id || businessId || `BUS-${numericId.toString(16).toUpperCase()}`,
          businessName: foundBusiness?.businessName || 'Northside Plumbing',
          tradingName: foundBusiness?.tradingName || 'Northside Plumbing',
          supportTickets: foundBusiness?.supportTickets ?? foundBusiness?.openSupportTickets ?? 2,
          accountScore: foundBusiness?.accountScore ?? foundBusiness?.businessScore ?? 100,
          businessScore: foundBusiness?.businessScore ?? 100,
          abn: foundBusiness?.abn || '53004085616',
          email: foundBusiness?.email || 'northside.plumbing@example.com',
          businessEmail: foundBusiness?.businessEmail || foundBusiness?.email,
          mobile: foundBusiness?.mobile || '0412 678 932',
          landline: foundBusiness?.landline || '(03) 9123 4567',
          website: foundBusiness?.website || 'www.northsideplumbing.com.au',
          address: foundBusiness?.address || '123 Main Street',
          emailVerified: foundBusiness?.emailVerified ?? true,
          phoneVerified: foundBusiness?.phoneVerified ?? true,
          abnVerified: foundBusiness?.abnVerified ?? true,
          abnInvalid: foundBusiness?.abnInvalid ?? false,
          abnVerifiedDate: foundBusiness?.abnVerifiedDate || foundBusiness?.abnDetails?.verifiedOn || '15 Aug 2025',
          licenseVerified: foundBusiness?.licenseVerified ?? true,
          insuranceVerified: foundBusiness?.insuranceVerified ?? true,
          ticketVerified: foundBusiness?.ticketVerified ?? true,
          badgeVerified: foundBusiness?.badgeVerified ?? true,
          emailVerifiedDate: foundBusiness?.emailVerifiedDate || '12 Aug 2025',
          phoneVerifiedDate: foundBusiness?.phoneVerifiedDate || '12 Aug 2025',
          region: foundBusiness?.region || foundBusiness?.state || 'VIC',
          location: foundBusiness?.location || {
            suburb: 'Clayton',
            postcode: '3168',
            state: 'VIC',
            fullAddress: '123 Main Street, Clayton VIC 3168'
          },
          plan: foundBusiness?.plan || 'PRO',
          status: foundBusiness?.status || 'Active',
          verificationStatus: foundBusiness?.verificationStatus || 'Verified',
          flagged: foundBusiness?.flagged ?? false,
          suspended: foundBusiness?.suspended ?? false,
          strikes: foundBusiness?.strikes ?? 0,
          joined: foundBusiness?.joined || joinedDate.toISOString().split('T')[0],
          lastLogin: foundBusiness?.lastLogin || lastLoginDate.toISOString(),
          lastActive: foundBusiness?.lastActive || foundBusiness?.lastLogin || lastLoginDate.toISOString(),
          restrictionStatus: foundBusiness?.restrictionStatus ?? 'None',
          restrictionAppliedOn: foundBusiness?.restrictionAppliedOn ?? null,
          restrictionExpiresOn: foundBusiness?.restrictionExpiresOn ?? null,
          linkedCaseId: foundBusiness?.linkedCaseId ?? null,
          latestFlagId: foundBusiness?.latestFlagId ?? null,
          currentReviewStatus: foundBusiness?.currentReviewStatus ?? null,
          overviewOpenFlagsCount: foundBusiness?.overviewOpenFlagsCount,
          overviewPosts: foundBusiness?.overviewPosts,
          overviewFeaturedPosts: foundBusiness?.overviewFeaturedPosts,
          overviewQuotesSent: foundBusiness?.overviewQuotesSent,
          overviewReviewsReceived: foundBusiness?.overviewReviewsReceived,
          overviewAverageRating: foundBusiness?.overviewAverageRating,
          activeLicencesCount: foundBusiness?.activeLicencesCount,
          activeBadgesCount: foundBusiness?.activeBadgesCount,
          activeTicketsCount: foundBusiness?.activeTicketsCount,
          loginStatus: foundBusiness?.loginStatus || 'Active',
          lastIpAddress: foundBusiness?.lastIpAddress || '203.45.118.92',
          lastLocation: foundBusiness?.lastLocation || 'Melbourne, VIC',
          trustedDevices: foundBusiness?.trustedDevices ?? foundBusiness?.devices?.length ?? 3,
          profilePicture: foundBusiness?.profilePicture || `https://i.pravatar.cc/150?img=${numericId}`,
          
          // Business Health (last 30 days unless stated)
          sessions7d: 3,
          sessions30d: 9,
          sessions90d: 22,
          activePosts: 12,
          newQuoteRequests: 8,
          messagesThreadsStarted: 5,
          reviewsReceived: 34,
          averageRating: 4.7,
          standardPostsActive: 3,
          standardPostsLimit: 3,
          featuredPostsActive: 7,
          featuredPostsLimit: 10,
          licenseInsuranceDaysToExpiry: 45, // days to expiry (soonest document)
          
          // Recent Posts (last 3)
          recentPosts: [
            { id: 'POST-1', title: 'Emergency Plumbing Services', status: 'Active', lastRefreshed: '15 Oct 2025' },
            { id: 'POST-2', title: 'Kitchen Renovation Specialist', status: 'Active', lastRefreshed: '10 Oct 2025' },
            { id: 'POST-3', title: 'Bathroom Tiling Services', status: 'Flagged', lastRefreshed: '05 Oct 2025' }
          ],
          
          // Verification & Compliance
          abnDetails: {
            number: '53004085616',
            legalEntityName: 'Northside Plumbing Pty Ltd',
            status: 'Approved',
            verifiedOn: '15 Aug 2025',
            approvedBy: 'Admin: Rachel Brown',
            uploadedFiles: [
              { name: 'abn-certificate.pdf', date: '12 Aug 2025', url: 'https://example.com/documents/abn-certificate.pdf' },
              { name: 'business-registration.pdf', date: '12 Aug 2025', url: 'https://example.com/documents/business-registration.pdf' }
            ]
          },
          licenses: [
            {
              id: 'LIC-1',
              type: 'Electrical',
              number: 'ELEC-12345',
              issuingState: 'VIC',
              expiry: '2026-12-31',
              status: 'Approved',
              documents: [
                { name: 'electrical-license.pdf', date: '01 Jan 2025', url: 'https://example.com/documents/electrical-license.pdf' }
              ]
            },
            {
              id: 'LIC-2',
              type: 'Plumbing',
              number: 'PLUM-67890',
              issuingState: 'VIC',
              expiry: '2026-06-30',
              status: 'Approved',
              documents: [
                { name: 'plumbing-license.pdf', date: '15 Jan 2025', url: 'https://example.com/documents/plumbing-license.pdf' }
              ]
            }
          ],
          tickets: [
            {
              id: 'TKT-1',
              type: 'Working at Heights',
              number: 'WTH-12345',
              expiry: '2026-03-31',
              status: 'Approved',
              documents: [
                { name: 'wth-certificate.pdf', date: '01 Feb 2025', url: 'https://example.com/documents/wth-certificate.pdf' }
              ]
            },
            {
              id: 'TKT-2',
              type: 'Scaffolding',
              number: 'SCF-67890',
              expiry: '2026-09-30',
              status: 'Pending',
              documents: [
                { name: 'scaffolding-certificate.pdf', date: '15 Feb 2025', url: 'https://example.com/documents/scaffolding-certificate.pdf' }
              ]
            }
          ],
          badges: [
            {
              id: 'BDG-1',
              type: 'White Card',
              number: 'WC-12345',
              expiry: '2027-01-31',
              status: 'Approved',
              documents: [
                { name: 'white-card.pdf', date: '01 Mar 2025', url: 'https://example.com/documents/white-card.pdf' }
              ]
            },
            {
              id: 'BDG-2',
              type: 'First Aid',
              number: 'FA-67890',
              expiry: '2026-08-30',
              status: 'Approved',
              documents: [
                { name: 'first-aid-certificate.pdf', date: '15 Mar 2025', url: 'https://example.com/documents/first-aid-certificate.pdf' }
              ]
            }
          ],
          insurance: {
            policyType: 'Public Liability',
            policyNumber: 'PL-987654',
            insurer: 'AAMI Insurance',
            coverageAmount: '$5,000,000',
            expiry: '2026-03-31',
            status: 'Approved',
            certificate: { name: 'insurance-certificate.pdf', date: '01 Apr 2025', url: 'https://example.com/documents/insurance-certificate.pdf' }
          },
          complianceFlags: {
            fraud: false,
            misconduct: false,
            strikeCount: 0
          },
          
          // Subscription & Billing
          subscription: {
            plan: foundBusiness?.plan || 'PRO',
            planStatus: foundBusiness?.subscription?.planStatus || 'Active',
            billingCycle: foundBusiness?.subscription?.billingCycle || foundBusiness?.planType || 'Monthly',
            startDate: foundBusiness?.subscription?.startDate || '2 Aug 2026',
            renewalDate: foundBusiness?.subscription?.renewalDate || '2 Sep 2026',
            currentPeriodStart: '2 Aug 2026',
            currentPeriodEnd: '1 Sep 2026',
            currentBillingPeriod: foundBusiness?.currentBillingPeriod || '2 Aug 2026 – 1 Sep 2026',
            paymentStatus: foundBusiness?.paymentStatus || 'Succeeded',
            nextBillingDate: foundBusiness?.nextBillingDate || '2 Sep 2026',
            failedPaymentGracePeriod: foundBusiness?.failedPaymentGracePeriod || 'Not applicable',
            gracePeriodEndDate: foundBusiness?.gracePeriodEndDate || 'Not applicable',
            paymentMethod: 'Visa •••• 1234'
          },
          subscriptionStatus:
            foundBusiness?.subscription?.planStatus ||
            foundBusiness?.planStatus ||
            'Active',
          currentBillingPeriod: foundBusiness?.currentBillingPeriod || '2 Aug 2026 – 1 Sep 2026',
          paymentStatus: foundBusiness?.paymentStatus || 'Succeeded',
          nextBillingDate: foundBusiness?.nextBillingDate || '2 Sep 2026',
          failedPaymentGracePeriod: foundBusiness?.failedPaymentGracePeriod || 'Not applicable',
          gracePeriodEndDate: foundBusiness?.gracePeriodEndDate || 'Not applicable',
          accountScore: foundBusiness?.accountScore ?? foundBusiness?.businessScore ?? 100,
          openSupportTickets: foundBusiness?.openSupportTickets ?? foundBusiness?.supportTickets ?? 2,
          activeAppeals: foundBusiness?.activeAppeals ?? 1,
          overviewMessages: foundBusiness?.overviewMessages ?? foundBusiness?.messages30d ?? 18,
          subscriptionHistory: [
            {
              id: 'SUB-HIST-1',
              date: '15 Aug 2025',
              event: 'Upgraded to PRO',
              plan: 'PRO',
              performedBy: 'Customer'
            },
            {
              id: 'SUB-HIST-2',
              date: '01 Jul 2025',
              event: 'Started Basic',
              plan: 'Basic',
              performedBy: 'System'
            }
          ],
          invoices: [
            {
              id: 'INV-1',
              invoiceId: 'INV-2025-001',
              date: '15 Oct 2025',
              amount: '$49.00',
              status: 'Paid',
              method: 'Visa •••• 1234',
              url: 'https://example.com/invoices/INV-2025-001'
            },
            {
              id: 'INV-2',
              invoiceId: 'INV-2025-002',
              date: '15 Sep 2025',
              amount: '$49.00',
              status: 'Paid',
              method: 'Visa •••• 1234',
              url: 'https://example.com/invoices/INV-2025-002'
            },
            {
              id: 'INV-3',
              invoiceId: 'INV-2025-003',
              date: '15 Aug 2025',
              amount: '$49.00',
              status: 'Paid',
              method: 'Visa •••• 1234',
              url: 'https://example.com/invoices/INV-2025-003'
            },
            {
              id: 'INV-4',
              invoiceId: 'INV-2025-004',
              date: '01 Jul 2025',
              amount: '$19.00',
              status: 'Paid',
              method: 'Visa •••• 1234',
              url: 'https://example.com/invoices/INV-2025-004'
            }
          ],
          transactions: [
            {
              id: 'TXN-1',
              transactionId: 'TXN-2025-001',
              type: 'Subscription',
              invoiceId: 'INV-2025-001',
              invoiceUrl: 'https://example.com/invoices/INV-2025-001',
              amount: '$49.00',
              status: 'Paid',
              date: '15 Oct 2025',
              paymentMethod: 'Visa •••• 1234',
              providerRef: 'ch_3QxYz1K2J8K9L0M1N2O3P4Q5'
            },
            {
              id: 'TXN-2',
              transactionId: 'TXN-2025-002',
              type: 'Renewal',
              invoiceId: 'INV-2025-002',
              invoiceUrl: 'https://example.com/invoices/INV-2025-002',
              amount: '$49.00',
              status: 'Paid',
              date: '15 Sep 2025',
              paymentMethod: 'Visa •••• 1234',
              providerRef: 'ch_3QxYz1K2J8K9L0M1N2O3P4Q6'
            },
            {
              id: 'TXN-3',
              transactionId: 'TXN-2025-003',
              type: 'Renewal',
              invoiceId: 'INV-2025-003',
              invoiceUrl: 'https://example.com/invoices/INV-2025-003',
              amount: '$49.00',
              status: 'Paid',
              date: '15 Aug 2025',
              paymentMethod: 'Visa •••• 1234',
              providerRef: 'ch_3QxYz1K2J8K9L0M1N2O3P4Q7'
            },
            {
              id: 'TXN-4',
              transactionId: 'TXN-2025-004',
              type: 'Adjustment',
              invoiceId: null,
              invoiceUrl: null,
              amount: '-$10.00',
              status: 'Paid',
              date: '10 Aug 2025',
              paymentMethod: 'Credit',
              providerRef: 'adj_3QxYz1K2J8K9L0M1N2O3P4Q8'
            },
            {
              id: 'TXN-5',
              transactionId: 'TXN-2025-005',
              type: 'Refund',
              invoiceId: 'INV-2025-000',
              invoiceUrl: 'https://example.com/invoices/INV-2025-000',
              amount: '-$49.00',
              status: 'Refunded',
              date: '01 Aug 2025',
              paymentMethod: 'Visa •••• 1234',
              providerRef: 're_3QxYz1K2J8K9L0M1N2O3P4Q9'
            }
          ],
          lastPaymentDate: '15 Oct 2025',
          
          // Posts data
          posts: [
            {
              id: 'POST-1',
              title: 'Emergency Plumbing Services',
              serviceType: 'Emergency plumbing',
              category: 'Plumbing',
              postType: 'Featured',
              status: 'Published',
              flagStatus: 'None',
              created: '15 Sep 2025',
              lastUpdated: '15 Oct 2025',
              views30d: 234
            },
            {
              id: 'POST-2',
              title: 'Kitchen Renovation Specialist',
              serviceType: 'Kitchen renovation',
              category: 'Renovation',
              postType: 'Standard',
              status: 'Published',
              flagStatus: 'None',
              created: '10 Sep 2025',
              lastUpdated: '10 Oct 2025',
              views30d: 189
            },
            {
              id: 'POST-3',
              title: 'Bathroom Tiling Services',
              serviceType: 'Bathroom tiling',
              category: 'Tiling',
              postType: 'Standard',
              status: 'Published',
              flagStatus: 'Flagged',
              created: '05 Sep 2025',
              lastUpdated: '05 Oct 2025',
              views30d: 156
            },
            {
              id: 'POST-4',
              title: 'Roof Repair Services',
              serviceType: 'Roof repair',
              category: 'Roofing',
              postType: 'Featured',
              status: 'Published',
              flagStatus: 'Under Review',
              created: '01 Sep 2025',
              lastUpdated: '01 Oct 2025',
              views30d: 98
            },
            {
              id: 'POST-5',
              title: 'Electrical Installation',
              serviceType: 'Electrical installation',
              category: 'Electrical',
              postType: 'Standard',
              status: 'Hidden',
              flagStatus: 'None',
              created: '28 Aug 2025',
              lastUpdated: '28 Sep 2025',
              views30d: 67
            },
            {
              id: 'POST-6',
              title: 'Drain Cleaning Services',
              serviceType: 'Drain cleaning',
              category: 'Plumbing',
              postType: 'Standard',
              status: 'Removed',
              flagStatus: 'Resolved',
              created: '25 Aug 2025',
              lastUpdated: '25 Aug 2025',
              views30d: 0
            },
            {
              id: 'POST-7',
              title: 'Garden Landscaping',
              serviceType: 'Landscaping',
              category: 'Landscaping',
              postType: 'Standard',
              status: 'Pending',
              flagStatus: 'None',
              created: '20 Aug 2025',
              lastUpdated: '20 Aug 2025',
              views30d: 0
            }
          ],
          
          // Activity stats (30 days)
          activeSessions: 9,
          messagesStarted: 3,
          quoteRequestsCount: 2,
          reviewsPosted: 1,
          retention30Days: true,
          avgSessionsPerWeek: 2.3,
          
          // Sessions & Usage
          sessions7d: 3,
          sessions30d: 9,
          sessions90d: 22,
          avgSessionDuration: '6m 14s',
          totalSessions: foundBusiness?.totalSessions || 45,
          failedLogins: 3,
          failedLoginAttempts: foundBusiness?.failedLoginAttempts || 3,
          passwordLastChanged: passwordLastChangedDate.toISOString(),
          passwordResetLogs: passedBusiness?.passwordResetLogs || [
            { date: '10 Sep 2025', type: 'Admin Reset' },
            { date: '15 Aug 2025', type: 'User Requested' }
          ],
          
          // Engagement (7d | 30d | 90d)
          messages7d: 1,
          messages30d: 3,
          messages90d: 8,
          quotes7d: 1,
          quotes30d: 2,
          quotes90d: 6,
          reviews7d: 0,
          reviews30d: 1,
          reviews90d: 3,
          
          // Cohort
          signupMonth: 'Aug 2025',
          retained30to60d: true,
          
          // Reviews
          totalReviews: 92,
          averageRating: 4.8,
          ratingBreakdown: {
            five: { count: 2200, percentage: 75 },
            four: { count: 550, percentage: 20 },
            three: { count: 550, percentage: 5 }
          },
          aiReviewAnalysis: {
            summary: 'Customers are satisfied with the business, noting its quality service, professionalism, and quick, helpful support.',
            sentiment: 'positive'
          },
          
          // Security
          loginStatus: foundBusiness?.loginStatus || 'Active',
          mfaEnabled: foundBusiness?.mfaEnabled ?? false,
          lastIP: foundBusiness?.lastIP || '203.45.118.92',
          lastIpAddress: foundBusiness?.lastIpAddress || '203.45.118.92',
          lastIPCountry: foundBusiness?.lastIPCountry || 'AU',
          lastLogin: foundBusiness?.lastLogin || '2026-01-29T14:22:00',
          lastLocation: foundBusiness?.lastLocation || 'Melbourne, VIC',
          failedLoginAttempts24h: foundBusiness?.failedLoginAttempts24h ?? 1,
          failedLoginAttempts7Days: foundBusiness?.failedLoginAttempts7Days ?? 4,
          trustedDevices: foundBusiness?.trustedDevices ?? 3,
          currentActiveSessions: foundBusiness?.currentActiveSessions ?? 2,
          lastDeviceUsed: foundBusiness?.lastDeviceUsed || 'MacBook Pro · Chrome',
          lastDeviceAdded: foundBusiness?.lastDeviceAdded || '2026-01-18',
          twoFAStatus: foundBusiness?.twoFAStatus || (foundBusiness?.mfaEnabled ? 'Enabled' : 'Disabled'),
          twoFALastUpdated: foundBusiness?.twoFALastUpdated || '12 Aug 2025',
          canLogin: foundBusiness?.canLogin !== undefined ? foundBusiness.canLogin : true,
          canPost: foundBusiness?.canPost !== undefined ? foundBusiness.canPost : true,
          canReceiveQuotes: foundBusiness?.canReceiveQuotes !== undefined ? foundBusiness.canReceiveQuotes : true,
          canSendMessages: foundBusiness?.canSendMessages !== undefined ? foundBusiness.canSendMessages : true,
          
          // Recent items for overview
          recentMessages: [
            { customer: 'John Smith', date: '23 Oct 2025', status: 'Open' },
            { customer: 'Sarah Johnson', date: '10 Oct 2025', status: 'Closed' },
            { customer: 'Mike Brown', date: '05 Oct 2025', status: 'Open' }
          ],
          recentQuotes: [
            { customer: 'John Smith', date: '20 Oct 2025', status: 'Replied' },
            { customer: 'Sarah Johnson', date: '10 Oct 2025', status: 'Sent' },
            { customer: 'Mike Brown', date: '02 Oct 2025', status: 'Replied' }
          ],
          latestReviews: [
            { customer: 'John Smith', rating: 5, date: '21 Oct 2025' },
            { customer: 'Sarah Johnson', rating: 4, date: '04 Oct 2025' },
            { customer: 'Mike Brown', rating: 5, date: '14 Sep 2025' }
          ],
          
          // Reviews data
          reviews: [
            { 
              id: 'RV-1',
              orderId: 'Order-12567',
              date: '21 Oct 2025',
              postedDate: 'Oct 21, 2025',
              customer: 'John Smith',
              customerSuburb: 'Clayton',
              customerProfilePicture: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
              customerId: 'CUS-3920',
              rating: 5,
              title: 'Great service',
              status: 'Published',
              fullText: 'I recently needed plumbing work done and Northside Plumbing was fantastic. The plumber arrived on time, was professional, and completed the work efficiently. Highly recommended!',
              reviewSnippet: 'I recently needed plumbing work done and Northside Plumbing was fantastic. The plumber arrived on time, was professional...',
              tags: ['Mixer repair', 'Kitchen plumbing'],
              mediaCount: 3,
              replyDate: '22 Oct 2025',
              images: [
                'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=200&h=200&fit=crop',
                'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=200&h=200&fit=crop',
                'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=200&h=200&fit=crop'
              ],
              videos: [],
              moderationNotes: '',
              readMore: true
            },
            { 
              id: 'RV-2',
              orderId: 'Order-12901',
              date: '10 Oct 2025',
              postedDate: 'Oct 10, 2025',
              customer: 'Sarah Johnson',
              customerSuburb: 'Richmond',
              customerProfilePicture: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
              customerId: 'CUS-1187',
              rating: 4,
              title: 'Quick & tidy',
              status: 'Published',
              fullText: 'They fixed my electrical issue quickly and left the place clean. Good service overall. The technician was knowledgeable and explained everything clearly.',
              reviewSnippet: 'They fixed my electrical issue quickly and left the place clean. Good service overall. The technician was knowledgeable...',
              tags: ['Switchboard upgrade', 'Electrical repair'],
              mediaCount: 1,
              replyDate: null,
              images: [
                'https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=200&h=200&fit=crop'
              ],
              videos: [],
              moderationNotes: '',
              readMore: true
            },
            { 
              id: 'RV-3',
              orderId: 'Order-12045',
              date: '02 Oct 2025',
              postedDate: 'Oct 2, 2025',
              customer: 'Mike Brown',
              customerSuburb: 'Footscray',
              customerProfilePicture: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop',
              customerId: 'CUS-2203',
              rating: 3,
              title: 'Average service',
              status: 'Flagged',
              fullText: 'The service was okay but could have been better. Some issues with communication.',
              reviewSnippet: 'The service was okay but could have been better. Some issues with communication.',
              tags: ['Heating repair'],
              mediaCount: 0,
              replyDate: null,
              item: 'Heating Service',
              images: [],
              videos: [],
              moderationNotes: '',
              readMore: false
            },
            { 
              id: 'RV-4',
              orderId: 'Order-11580',
              date: '28 Sep 2025',
              postedDate: 'Sep 28, 2025',
              customer: 'Emma Wilson',
              customerSuburb: 'Brunswick',
              customerProfilePicture: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
              customerId: 'CUS-9401',
              rating: 4,
              title: 'On time',
              status: 'Removed',
              fullText: 'They arrived exactly when they said they would. Good work and fair pricing.',
              reviewSnippet: 'They arrived exactly when they said they would. Good work and fair pricing.',
              tags: ['Roof repair', 'Tile replacement'],
              mediaCount: 0,
              replyDate: '29 Sep 2025',
              item: 'Roof Tile Replacement',
              images: [],
              videos: [],
              moderationNotes: 'Policy violation (language)',
              readMore: false
            },
            { 
              id: 'RV-5',
              orderId: 'Order-11200',
              date: '25 Sep 2025',
              postedDate: 'Sep 25, 2025',
              customer: 'David Lee',
              customerSuburb: 'Hawthorn',
              customerProfilePicture: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
              customerId: 'CUS-5501',
              rating: 5,
              title: 'Excellent work',
              status: 'Published',
              fullText: 'Professional service and excellent quality work. Would definitely use again.',
              reviewSnippet: 'Professional service and excellent quality work. Would definitely use again.',
              tags: ['Kitchen tiling', 'Bathroom renovation'],
              mediaCount: 2,
              replyDate: '26 Sep 2025',
              item: 'Carpentry Service',
              images: [
                'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=200&h=200&fit=crop',
                'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=200&h=200&fit=crop'
              ],
              videos: [],
              moderationNotes: '',
              readMore: false
            },
            { 
              id: 'RV-6',
              orderId: 'Order-11050',
              date: '20 Sep 2025',
              postedDate: 'Sep 20, 2025',
              customer: 'Lisa Chen',
              customerSuburb: 'St Kilda',
              customerProfilePicture: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop',
              customerId: 'CUS-6602',
              rating: 5,
              title: 'Very satisfied',
              status: 'Pending',
              fullText: 'Great experience from start to finish. The team was professional and the work was completed on time.',
              reviewSnippet: 'Great experience from start to finish. The team was professional and the work was completed on time.',
              tags: ['Interior painting', 'Wall repair'],
              mediaCount: 1,
              replyDate: null,
              item: 'Painting Service',
              images: [
                'https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=200&h=200&fit=crop'
              ],
              videos: [],
              moderationNotes: '',
              readMore: false
            }
          ],
          
          // Quote Requests data
          quoteRequests: [
            {
              id: 'QR-1',
              quoteId: 'Q-4832',
              date: '20 Oct 2025',
              customer: 'John S.',
              customerProfilePicture: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
              customerLocation: 'Clayton 3168',
              title: 'Replace kitchen mixer',
              location: 'Clayton 3168',
              status: 'Accepted (Hired)'
            },
            {
              id: 'QR-2',
              quoteId: 'Q-4815',
              date: '10 Oct 2025',
              customer: 'Sarah J.',
              customerProfilePicture: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
              customerLocation: 'Richmond 3121',
              title: 'Downlight install (6)',
              location: 'Richmond 3121',
              status: 'Viewed'
            },
            {
              id: 'QR-3',
              quoteId: 'Q-4798',
              date: '02 Oct 2025',
              customer: 'Mike B.',
              customerProfilePicture: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop',
              customerLocation: 'Footscray 3011',
              title: 'Roof repair - small leak',
              location: 'Footscray 3011',
              status: 'Sent'
            },
            {
              id: 'QR-4',
              quoteId: 'Q-4772',
              date: '28 Sep 2025',
              customer: 'Emma W.',
              customerProfilePicture: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
              customerLocation: 'Brunswick 3056',
              title: 'Gas heater service',
              location: 'Brunswick 3056',
              status: 'Declined'
            },
            {
              id: 'QR-5',
              quoteId: 'Q-4755',
              date: '25 Sep 2025',
              customer: 'David L.',
              customerProfilePicture: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
              customerLocation: 'Hawthorn 3122',
              title: 'Kitchen cabinet installation',
              location: 'Hawthorn 3122',
              status: 'Withdrawn'
            },
            {
              id: 'QR-6',
              quoteId: 'Q-4731',
              date: '18 Sep 2025',
              customer: 'Lisa C.',
              customerProfilePicture: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop',
              customerLocation: 'St Kilda 3182',
              title: 'Interior painting (3 rooms)',
              location: 'St Kilda 3182',
              status: 'Expired'
            },
            {
              id: 'QR-8',
              date: '10 Sep 2025',
              customer: 'Amy Taylor',
              jobTitle: 'Door lock replacement',
              status: 'Replied'
            },
            {
              id: 'QR-9',
              date: '05 Sep 2025',
              customer: 'Chris Martinez',
              jobTitle: 'Bathroom tiling',
              status: 'Replied'
            },
            {
              id: 'QR-10',
              date: '01 Sep 2025',
              customer: 'Jessica White',
              jobTitle: 'Window cleaning service',
              status: 'Sent'
            },
            {
              id: 'QR-11',
              date: '28 Aug 2025',
              customer: 'Robert Davis',
              jobTitle: 'Deck construction',
              status: 'Replied'
            },
            {
              id: 'QR-12',
              date: '22 Aug 2025',
              customer: 'Michelle Garcia',
              jobTitle: 'Hardwood floor installation',
              status: 'Sent'
            }
          ],
          
          // Message Threads data
          messageThreads: [
            {
              id: 'MSG-1',
              started: '22 Oct 2025',
              customer: 'John S.',
              customerId: 'CUS-001',
              customerProfilePicture: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
              customerLocation: 'Clayton 3168',
              title: 'Replace kitchen mixer',
              lastMessage: '23 Oct 2025',
              lastMessageText: 'Can you come Saturday?',
              status: 'Unseen'
            },
            {
              id: 'MSG-2',
              started: '09 Oct 2025',
              customer: 'Sarah J.',
              customerId: 'CUS-002',
              customerProfilePicture: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
              customerLocation: 'Fitzroy 3065',
              title: 'Downlight install (6)',
              lastMessage: '10 Oct 2025',
              lastMessageText: 'Thanks for the quick response!',
              status: 'Seen'
            },
            {
              id: 'MSG-3',
              started: '05 Oct 2025',
              customer: 'Mike B.',
              customerId: 'CUS-003',
              customerProfilePicture: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop',
              customerLocation: 'Footscray 3011',
              title: 'Roof repair - small leak',
              lastMessage: '06 Oct 2025',
              lastMessageText: 'What time works best for you?',
              status: 'Unseen'
            },
            {
              id: 'MSG-4',
              started: '30 Sep 2025',
              customer: 'Emma W.',
              customerId: 'CUS-004',
              customerProfilePicture: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
              customerLocation: 'Brunswick 3056',
              title: 'Gas heater service',
              lastMessage: '01 Oct 2025',
              lastMessageText: 'The quote looks good, let me check my schedule',
              status: 'Seen'
            },
            {
              id: 'MSG-5',
              started: '25 Sep 2025',
              customer: 'David L.',
              customerId: 'CUS-005',
              customerProfilePicture: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
              customerLocation: 'Hawthorn 3122',
              title: 'Kitchen cabinet installation',
              lastMessage: '26 Sep 2025',
              lastMessageText: 'I have a few questions about the color options',
              status: 'Unseen'
            },
            {
              id: 'MSG-6',
              started: '20 Sep 2025',
              customer: 'Lisa C.',
              customerId: 'CUS-006',
              customerProfilePicture: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop',
              customerLocation: 'St Kilda 3182',
              title: 'Interior painting (3 rooms)',
              lastMessage: '21 Sep 2025',
              lastMessageText: 'Perfect, see you next week!',
              status: 'Seen'
            },
            {
              id: 'MSG-8',
              started: '10 Sep 2025',
              customer: 'Amy Taylor',
              customerId: 'CUS-008',
              lastMessage: '11 Sep 2025',
              lastMessageText: 'When can you start the work?',
              status: 'Unseen'
            },
            {
              id: 'MSG-9',
              started: '05 Sep 2025',
              customer: 'Chris Martinez',
              customerId: 'CUS-009',
              lastMessage: '06 Sep 2025',
              lastMessageText: 'The price seems reasonable',
              status: 'Seen'
            },
            {
              id: 'MSG-10',
              started: '01 Sep 2025',
              customer: 'Jessica White',
              customerId: 'CUS-010',
              lastMessage: '02 Sep 2025',
              lastMessageText: 'Can you provide more details?',
              status: 'Unseen'
            },
            {
              id: 'MSG-11',
              started: '28 Aug 2025',
              customer: 'Robert Davis',
              customerId: 'CUS-011',
              lastMessage: '29 Aug 2025',
              lastMessageText: 'Looking forward to working with you',
              status: 'Seen'
            },
            {
              id: 'MSG-12',
              started: '22 Aug 2025',
              customer: 'Michelle Garcia',
              customerId: 'CUS-012',
              lastMessage: '23 Aug 2025',
              lastMessageText: 'Thanks for the update',
              status: 'Seen'
            }
          ],
          
          // Security - Devices
          devices: [
            {
              date: '23 Oct 2025 14:22',
              device: 'Safari on macOS',
              location: 'Melbourne, AU'
            },
            {
              date: '18 Oct 2025 21:10',
              device: 'Chrome on Android 14',
              location: 'Melbourne, AU'
            },
            {
              date: '15 Oct 2025 09:30',
              device: 'Chrome on Windows',
              location: 'Melbourne, AU'
            },
            {
              date: '10 Oct 2025 16:45',
              device: 'Safari on iOS',
              location: 'Sydney, AU'
            },
            {
              date: '05 Oct 2025 11:20',
              device: 'Firefox on Windows',
              location: 'Melbourne, AU'
            }
          ],
          
          // Verification Events
          emailVerifySent: '12 Aug 2025 08:14',
          emailVerifyCompleted: '12 Aug 2025 08:16',
          phoneVerifySent: '12 Aug 2025 08:20',
          phoneVerifyCompleted: '12 Aug 2025 08:21',
          
          // Audit Log — record-level history only (not system/message/quote noise); 4 rows per scenario
          auditLogs: (() => {
            const scenarioIndex = numericId % 3;
            const scenarios = [
              [
                { id: 'LOG-1', timestamp: '2025-10-10T09:00:00', performedBy: 'Rachel Brown', userType: 'Admin Manager', action: 'Business Flag Created', section: 'Flags', summary: 'High-priority account flag created after repeated contact-sharing review' },
                { id: 'LOG-2', timestamp: '2025-11-15T14:30:00', performedBy: 'Mark Johnson', userType: 'Compliance', action: 'Account Status Changed', section: 'Compliance Summary', summary: 'Business moved to Under Review while licence details were rechecked' },
                { id: 'LOG-3', timestamp: '2025-11-17T09:15:00', performedBy: 'Sarah Lee', userType: 'Support', action: 'Internal Note Added', section: 'Admin Notes', summary: 'Support note added after business reported delayed public profile update' },
                { id: 'LOG-4', timestamp: '2025-11-02T08:42:00', performedBy: 'Business', userType: 'Business', action: 'Account Created', section: 'Onboarding', summary: 'Business account created successfully during signup' }
              ],
              [
                { id: 'LOG-1', timestamp: '2025-09-01T11:00:00', performedBy: 'System', userType: 'System', action: 'Plan Changed', section: 'Subscription & Billing', summary: 'Subscription upgraded from Basic to PRO for the billing account' },
                { id: 'LOG-2', timestamp: '2025-10-05T16:20:00', performedBy: 'John Smith', userType: 'Moderator', action: 'Messaging Restriction Applied', section: 'Messaging', summary: 'Temporary restriction after policy breach on contact-sharing in threads' },
                { id: 'LOG-3', timestamp: '2025-10-12T10:00:00', performedBy: 'Mark Johnson', userType: 'Compliance', action: 'Compliance Hold Applied', section: 'Compliance Summary', summary: 'Hold applied pending verification of trade licence documentation' },
                { id: 'LOG-4', timestamp: '2025-10-20T14:45:00', performedBy: 'Sarah Lee', userType: 'Support', action: 'Key Business Details Updated', section: 'Business Profile', summary: 'Trading name, service areas, and public phone updated after owner request' }
              ],
              [
                { id: 'LOG-1', timestamp: '2025-11-01T09:30:00', performedBy: 'Alex Roberts', userType: 'Moderator', action: 'Business Flag Resolved', section: 'Flags', summary: 'Flag cleared after owner addressed profile policy concerns' },
                { id: 'LOG-2', timestamp: '2025-10-28T11:15:00', performedBy: 'Rachel Brown', userType: 'Admin Manager', action: 'Login Restriction Removed', section: 'Security', summary: 'Restriction lifted following verified identity check and clean login history' },
                { id: 'LOG-3', timestamp: '2025-10-15T13:00:00', performedBy: 'Mark Johnson', userType: 'Compliance', action: 'ABN Details Updated', section: 'Verification', summary: 'ABN record refreshed after entity name change confirmation from ASIC' },
                { id: 'LOG-4', timestamp: '2025-09-20T08:00:00', performedBy: 'Sarah Lee', userType: 'Support', action: 'Business Reactivated', section: 'Account Status', summary: 'Account restored after outstanding verification items were completed' }
              ]
            ];
            return scenarios[scenarioIndex];
          })(),
          
          primaryFlagId: foundBusiness?.primaryFlagId || 'FLG-2088',
          moderationAndFlags: isProfileScenarioBusiness ? (foundBusiness.moderationAndFlags ?? foundBusiness.flags ?? []) : [
            {
              id: 'FLG-51001',
              flagId: 'FLG-51001',
              source: 'User Flag',
              type: 'User Flag',
              status: 'Active',
              severity: 'Medium',
              reason: 'Misleading service information',
              linkedCaseId: 'MOD-000601',
              linkedCaseType: 'Moderation',
              openedOn: '2026-08-01T00:20:00',
              lastUpdated: '2026-08-04T10:15:00',
            },
            {
              id: 'FLG-51012',
              flagId: 'FLG-51012',
              source: 'System Flag',
              type: 'System Flag',
              status: 'Active',
              severity: 'High',
              reason: 'Published profile policy check',
              linkedCaseId: 'MOD-000618',
              linkedCaseType: 'Moderation',
              openedOn: '2026-07-28T11:45:00',
              lastUpdated: '2026-08-02T08:30:00',
            },
            {
              id: 'FLG-51018',
              flagId: 'FLG-51018',
              source: 'User Flag',
              type: 'User Flag',
              status: 'New',
              severity: 'Low',
              reason: 'Contact sharing in thread',
              linkedCaseId: 'MOD-000630',
              linkedCaseType: 'Moderation',
              messageId: 'MSG-88421',
              object: { type: 'Message', id: 'MSG-88421', title: null },
              openedOn: '2026-07-20T13:20:00',
              lastUpdated: '2026-07-20T13:20:00',
            },
            {
              id: 'FLG-51022',
              flagId: 'FLG-51022',
              source: 'User Flag',
              type: 'User Flag',
              status: 'New',
              severity: 'Medium',
              reason: 'Quote content policy check',
              linkedCaseId: 'MOD-000641',
              linkedCaseType: 'Moderation',
              quoteId: 'QTE-22014',
              object: { type: 'Quote', id: 'QTE-22014', title: null },
              openedOn: '2026-07-15T09:00:00',
              lastUpdated: '2026-07-15T09:00:00',
            },
            {
              id: 'FLG-50990',
              flagId: 'FLG-50990',
              source: 'User Flag',
              type: 'User Flag',
              status: 'Decided',
              severity: 'Low',
              reason: 'Inappropriate content',
              linkedCaseId: 'MOD-000512',
              linkedCaseType: 'Moderation',
              object: { type: 'Review', id: 'RV-3', title: null },
              openedOn: '2026-06-05T09:00:00',
              lastUpdated: '2026-06-06T14:20:00',
            },
          ],
          
          // Admin Notes — author: Compliance, Support, or Moderator (no "User management")
          notes: [
            {
              id: 'NOTE-1',
              createdAt: '2025-11-17T09:15:00',
              adminName: 'Support — Sarah Lee',
              team: 'Support',
              noteContent: 'Business reported issue with verification process. Followed up and resolved. Business owner was satisfied with the response time.',
              category: 'Support',
              author: 'Sarah Lee',
              content: 'Business reported issue with verification process. Followed up and resolved. Business owner was satisfied with the response time.',
              timestamp: '17 Nov 2025, 09:15',
              date: '17 Nov 2025'
            },
            {
              id: 'NOTE-2',
              createdAt: '2025-11-15T14:30:00',
              adminName: 'Compliance — Mark Johnson',
              team: 'Compliance',
              noteContent: 'Business account flagged for review due to multiple verification attempts. Security hold applied. Business owner contacted and verified identity.',
              category: 'Risk / Fraud',
              author: 'Mark Johnson',
              content: 'Business account flagged for review due to multiple verification attempts. Security hold applied. Business owner contacted and verified identity.',
              timestamp: '15 Nov 2025, 14:30',
              date: '15 Nov 2025'
            },
            {
              id: 'NOTE-3',
              createdAt: '2025-11-10T11:20:00',
              adminName: 'Moderator — Alex Roberts',
              team: 'Moderator',
              noteContent: 'Business requested refund for cancelled subscription. Processed refund and updated billing records. Business owner confirmed receipt.',
              category: 'Billing',
              author: 'Alex Roberts',
              content: 'Business requested refund for cancelled subscription. Processed refund and updated billing records. Business owner confirmed receipt.',
              timestamp: '10 Nov 2025, 11:20',
              date: '10 Nov 2025'
            }
          ],
          
          // Business Activity
          activities: [
            {
              id: 'ACT-1',
              timestamp: '2 Nov 2025, 09:12 AM',
              action: 'Logged in. IP: 124.177.28.5 (Chrome – MacOS)',
              details: ''
            },
            {
              id: 'ACT-2',
              timestamp: '2 Nov 2025, 09:15 AM',
              action: 'Updated business profile',
              details: ''
            },
            {
              id: 'ACT-3',
              timestamp: '2 Nov 2025, 09:17 AM',
              action: 'Replied to message from John Smith',
              details: ''
            },
            {
              id: 'ACT-4',
              timestamp: '2 Nov 2025, 09:20 AM',
              action: 'Sent quote to Sarah Johnson',
              details: ''
            },
            {
              id: 'ACT-5',
              timestamp: '2 Nov 2025, 09:35 AM',
              action: 'Created new post',
              details: '',
              postId: 'POST-1',
              type: 'post'
            },
            {
              id: 'ACT-6',
              timestamp: '2 Nov 2025, 10:02 AM',
              action: 'Received review from Mike Brown (Rating: 5 stars)',
              details: '',
              reviewId: 'RV-1',
              type: 'review'
            },
            {
              id: 'ACT-7',
              timestamp: '2 Nov 2025, 10:10 AM',
              action: 'Replied to review from Emma Wilson',
              details: '',
              reviewId: 'RV-2',
              type: 'review'
            },
            {
              id: 'ACT-8',
              timestamp: '2 Nov 2025, 10:12 AM',
              action: 'Updated service pricing',
              details: ''
            },
            {
              id: 'ACT-9',
              timestamp: '2 Nov 2025, 10:18 AM',
              action: 'Reported customer: David Lee (Reason: Harassment)',
              details: ''
            },
            {
              id: 'ACT-10',
              timestamp: '2 Nov 2025, 10:21 AM',
              action: 'Admin closed report on David Lee (Report accepted)',
              details: ''
            },
            {
              id: 'ACT-11',
              timestamp: '2 Nov 2025, 10:28 AM',
              action: 'Requested account deactivation',
              details: ''
            },
            {
              id: 'ACT-12',
              timestamp: '2 Nov 2025, 10:30 AM',
              action: 'Deactivation cancelled by business',
              details: ''
            },
            {
              id: 'ACT-13',
              timestamp: '2 Nov 2025, 10:32 AM',
              action: 'Updated profile email address',
              details: ''
            },
            {
              id: 'ACT-14',
              timestamp: '2 Nov 2025, 10:40 AM',
              action: 'Phone number verified successfully',
              details: ''
            },
            {
              id: 'ACT-15',
              timestamp: '2 Nov 2025, 10:45 AM',
              action: 'Warning issued by Admin (Reason: policy violation)',
              details: ''
            },
            {
              id: 'ACT-16',
              timestamp: '2 Nov 2025, 10:46 AM',
              action: 'Business acknowledged warning',
              details: ''
            },
            {
              id: 'ACT-17',
              timestamp: '2 Nov 2025, 10:55 AM',
              action: 'Logged out',
              details: ''
            }
          ]
        };

        const accountRef =
          businessData.businessId || businessData.id || String(businessId || '');
        businessData.auditLogs = refreshBusinessAuditLogs(
          accountRef,
          businessData.auditLogs || []
        );
        
        setBusiness(businessData);
      } catch (error) {
        console.error('Error fetching business detail:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBusinessDetail();
  }, [businessId, location.state]);

  useEffect(() => {
    const tabFromState = location.state?.activeTab;
    if (tabFromState && ['overview', 'flags', 'security', 'audit-log'].includes(tabFromState)) {
      setActiveTab(tabFromState);
    }
  }, [location.state?.activeTab]);

  // Handle hash navigation to activate tabs
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        const hashToTabMap = {
          flags: 'flags',
          security: 'security',
          'audit-log': 'audit-log',
          'account-status': 'overview',
        };

        const tabId = hashToTabMap[hash];
        const validTabIds = ['overview', 'flags', 'security', 'audit-log'];
        if (tabId && validTabIds.includes(tabId)) {
          setActiveTab(tabId);
        }
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [location.hash]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Format datetime helper
  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return (
      date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) +
      ', ' +
      date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })
    );
  };

  const handleCopyBusinessId = async () => {
    const id = formatBusinessIdDisplay(business);
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      setBusinessIdCopied(true);
      window.setTimeout(() => setBusinessIdCopied(false), 1500);
    } catch {
      // Clipboard unavailable — leave UI unchanged
    }
  };

  const handleCopyContactField = async (field, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedContactField(field);
      window.setTimeout(() => setCopiedContactField(null), 1500);
    } catch {
      // Clipboard unavailable — leave UI unchanged
    }
  };

  const getOverviewLocation = (b) => {
    const suburb = b?.location?.suburb || b?.suburb || '';
    const state = b?.region || b?.location?.state || b?.state || '';
    const postcode = b?.location?.postcode || b?.postcode || '';
    if (suburb && state && postcode) return `${suburb}, ${state} ${postcode}`;
    if (suburb && postcode) return `${suburb} ${postcode}`;
    if (suburb && state) return `${suburb}, ${state}`;
    return suburb || state || postcode || '—';
  };

  const getOverviewTradeCategory = (b) => {
    const raw = b?.category ?? b?.primaryTrade ?? b?.tradeCategory ?? '';
    const first = Array.isArray(raw) ? raw[0] : String(raw).split(',')[0];
    const s = String(first || '').trim();
    if (!s) return '—';
    const lower = s.toLowerCase();
    if (lower.includes('plumb')) return 'Plumbing';
    if (lower.includes('electric')) return 'Electrical';
    if (lower.includes('hvac')) return 'HVAC & Refrigeration';
    if (lower.includes('roof')) return 'Roofing';
    if (lower.includes('landscape')) return 'Landscaping';
    if (lower.includes('paint')) return 'Painting';
    if (lower.includes('clean')) return 'Cleaning';
    if (lower.includes('handyman')) return 'Handyman';
    if (lower.includes('carpent')) return 'Carpentry';
    return s.replace(/\s*\/\s*Gas\s*$/i, '').trim() || '—';
  };

  const formatAbnDisplay = (b) => {
    const raw = String(b?.abn || '').replace(/\D/g, '');
    if (raw.length >= 9) {
      return `${raw.slice(0, 2)} ${raw.slice(2, 5)} ${raw.slice(5, 8)} ${raw.slice(8, 11)}`;
    }
    return b?.abn || '—';
  };

  const getOverviewPostsCount = (b) => {
    if (b?.overviewPosts != null) return Number(b.overviewPosts) || 0;
    if (Array.isArray(b?.posts)) return b.posts.filter((p) => p.status !== 'Removed').length;
    return (Number(b?.standardPostsActive) || 0) + (Number(b?.featuredPostsActive) || 0);
  };

  const getOverviewFeaturedPostsCount = (b) =>
    Number(b?.overviewFeaturedPosts ?? b?.featuredPostsActive ?? b?.featuredPosts ?? 0) || 0;

  const getOverviewReviewsCount = (b) =>
    Number(
      b?.overviewReviewsReceived ?? b?.reviewsReceived ?? b?.totalReviews ?? b?.reviews?.length ?? 0
    ) || 0;

  const getOverviewAverageRating = (b) => {
    const rating = b?.overviewAverageRating ?? b?.averageRating;
    if (rating == null || rating === '') return null;
    return rating;
  };

  const getOverviewMessagesCount = (b) =>
    Number(b?.overviewMessages ?? b?.messagesCount ?? b?.messages30d ?? b?.messagesStarted ?? 0) || 0;

  const getOverviewQuotesCount = (b) =>
    Number(
      b?.overviewQuotesSent ??
        b?.quotesSent ??
        b?.quotesCount ??
        b?.quoteRequestsCount ??
        b?.quotes30d ??
        0
    ) || 0;

  const getFlagSeverityTextClass = (severity) => {
    const s = String(severity || '').trim();
    if (s === 'High' || s === 'Critical' || s === 'Severe') return 'text-alertred';
    if (s === 'Medium') return 'text-warmyellow';
    if (s === 'Low') return 'text-freshgreen';
    return '';
  };

  const getOverviewSupportTickets = (b) =>
    Number(b?.openSupportTickets ?? b?.supportTickets ?? 0) || 0;

  const getOverviewActiveAppeals = (b) => Number(b?.activeAppeals ?? 0) || 0;

  const getApprovedComplianceNames = (items) => {
    if (!Array.isArray(items) || items.length === 0) return [];
    return items
      .filter((item) => {
        const st = String(item.status || '').toLowerCase();
        return st === 'approved' || st === 'active' || st === 'current' || !item.status;
      })
      .map((item) => item.name || item.type || item.title || item.label)
      .filter(Boolean);
  };

  const formatComplianceNames = (names) => {
    if (!names.length) return 'None';
    return names.join(', ');
  };

  const getBillingField = (b, key, fallback = '—') => {
    const sub = b?.subscription || {};
    const value = b?.[key] ?? sub[key];
    if (value == null || value === '') return fallback;
    return value;
  };

  const getCurrentBillingPeriod = (b) => {
    const period = getBillingField(b, 'currentBillingPeriod', null) || getBillingField(b, 'billingPeriod', null);
    if (period) return period;
    const start = b?.subscription?.startDate || b?.subscription?.currentPeriodStart;
    const end = b?.subscription?.renewalDate || b?.subscription?.currentPeriodEnd;
    if (start && end) return `${formatDate(start)} – ${formatDate(end)}`;
    return '—';
  };

  const renderOverviewLinkedField = ({
    label,
    valueText,
    enabled,
    onNavigate,
    valueClassName = '',
    linkValueClassName = '',
  }) => (
    <div className="space-y-1">
      <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
      {enabled ? (
        <AdminDetailFieldValue>
          <AdminLinkAction
            type="button"
            onClick={onNavigate}
            className={`tabular-nums text-left ${linkValueClassName}`.trim()}
          >
            {valueText}
          </AdminLinkAction>
        </AdminDetailFieldValue>
      ) : (
        <AdminDetailFieldValue className={`tabular-nums ${valueClassName || 'text-mediumgray'}`.trim()}>
          {valueText}
        </AdminDetailFieldValue>
      )}
    </div>
  );

  const renderCopyableValue = ({ fieldKey, displayValue, copyValue, verified }) => {
    if (!displayValue || displayValue === '—') {
      return <AdminDetailFieldValue>—</AdminDetailFieldValue>;
    }
    return (
      <div className="flex items-center gap-2 min-w-0">
        <AdminDetailFieldValue as="span" className="truncate min-w-0" title={displayValue}>
          {displayValue}
        </AdminDetailFieldValue>
        {verified ? (
          <i
            className="fi fi-sr-check-circle text-freshgreen text-xs leading-none shrink-0"
            title="Verified"
            aria-label="Verified"
          />
        ) : null}
        <button
          type="button"
          onClick={() => handleCopyContactField(fieldKey, copyValue || displayValue)}
          className="inline-flex items-center justify-center shrink-0 text-mediumgray hover:text-primaryblue"
          title={copiedContactField === fieldKey ? 'Copied' : `Copy ${fieldKey}`}
          aria-label={copiedContactField === fieldKey ? 'Copied' : `Copy ${fieldKey}`}
        >
          <i
            className={`fi ${
              copiedContactField === fieldKey ? 'fi-rr-check text-freshgreen' : 'fi-rr-copy'
            } text-xs leading-none`}
            aria-hidden
          />
        </button>
      </div>
    );
  };

  // Truncate text helper
  // Tags Display Component - dynamically shows tags that fit
  const TagsDisplay = ({ tags }) => {
    const containerRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(tags.length);
    const [remainingCount, setRemainingCount] = useState(0);

    useEffect(() => {
      const calculateVisibleTags = () => {
        if (!containerRef.current || tags.length === 0) return;

        const container = containerRef.current;
        const containerWidth = container.offsetWidth;
        const gap = 8; // gap-2 = 8px
        const plusTagWidth = 50; // Approximate width for "+X" tag
        
        let totalWidth = 0;
        let count = 0;

        // Create a temporary element to measure tag widths
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.visibility = 'hidden';
        tempDiv.style.whiteSpace = 'nowrap';
        tempDiv.className = 'border border-solid border-mediumgray rounded-full px-3 py-1 text-xs';
        document.body.appendChild(tempDiv);

        for (let i = 0; i < tags.length; i++) {
          tempDiv.textContent = tags[i];
          const tagWidth = tempDiv.offsetWidth;
          
          // Check if this tag + gap + plus tag (if needed) fits
          const neededWidth = totalWidth + tagWidth + (i > 0 ? gap : 0);
          const wouldNeedPlusTag = i < tags.length - 1;
          const totalNeeded = neededWidth + (wouldNeedPlusTag ? gap + plusTagWidth : 0);

          if (totalNeeded <= containerWidth) {
            totalWidth = neededWidth;
            count++;
          } else {
            break;
          }
        }

        document.body.removeChild(tempDiv);
        setVisibleCount(count);
        setRemainingCount(tags.length - count);
      };

      calculateVisibleTags();
      window.addEventListener('resize', calculateVisibleTags);
      return () => window.removeEventListener('resize', calculateVisibleTags);
    }, [tags]);

    return (
      <div ref={containerRef} className="flex items-center gap-2 overflow-hidden">
        {tags.slice(0, visibleCount).map((tag, tagIndex) => (
          <span 
            key={tagIndex} 
            className="border border-solid border-mediumgray rounded-full px-3 py-1 cursor-pointer transition-all duration-200 hover:bg-offblack hover:text-white hover:border-offblack text-xs text-offblack flex-shrink-0"
          >
            {tag}
          </span>
        ))}
        {remainingCount > 0 && (
          <span className="border border-solid border-mediumgray rounded-full px-3 py-1 text-xs text-offblack flex-shrink-0">
            +{remainingCount}
          </span>
        )}
      </div>
    );
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '—';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const buildBusinessDetailFlagRows = (b) => {
    const mapRow = (item, index) => {
      const idStr = String(item.id || '');
      const inferredCaseId =
        item.linkedCaseId ||
        item.caseId ||
        (item.type === 'Moderation Case' &&
        idStr &&
        !idStr.startsWith('FLG') &&
        !/^F-\d/i.test(idStr)
          ? item.id
          : null);
      const handling = resolveBusinessFlagHandling({
        ...item,
        linkedCaseId: inferredCaseId,
      });
      return {
        key: normalizeBusinessFlagId(item.flagId || item.id, index),
        flagId: normalizeBusinessFlagId(item.flagId || item.id, index),
        source: mapFlagSourceToSpec(item.source || item.type, item),
        reason: normalizeFlagReasonText(item),
        severity: normalizeFlagSeverity(item.severity),
        handlingRecord: handling.handlingRecord,
        handlingRecordId: handling.handlingRecordId,
        handlingStatus: handling.handlingStatus,
        actionLabel: handling.actionLabel,
        actionPath: handling.actionPath,
        openFromArchive: handling.openFromArchive,
        createdAt: item.openedOn || item.created || item.createdAt,
        updatedAt:
          item.lastUpdated || item.lastUpdatedAt || item.updatedAt || item.openedOn || item.created,
      };
    };

    if (b.moderationAndFlags?.length) {
      return b.moderationAndFlags.map(mapRow);
    }
    return (b.flags || []).map(mapRow);
  };

  const handleFlagRowAction = (flag) => {
    if (!flag?.actionPath) return;
    navigate(flag.actionPath, {
      state: {
        businessId: formatBusinessIdDisplay(business),
        flagId: flag.flagId,
        handlingRecordId: flag.handlingRecordId,
        fromArchive: !!flag.openFromArchive,
      },
    });
  };

  const getSecurityActiveSessions = (b) =>
    Number(b?.currentActiveSessions ?? b?.activeSessions ?? 0) || 0;

  const getSecurityBusinessDisplayName = (b) => b?.businessName || 'this business';

  const appendSecurityAuditLog = (prev, action, summary) => {
    const accountRef = prev.businessId || prev.id;
    recordAccountActivity({
      accountRef,
      accountType: 'Business',
      changeType: action,
      activityDetails: summary,
      changedBy: 'Admin — Sarah Lee',
      itemId: accountRef,
    });
    return mapCentralAuditToLocal(getAccountAuditLogRows(accountRef));
  };

  const openForceLogoutConfirm = () => {
    const sessions = getSecurityActiveSessions(business);
    if (sessions <= 0) return;
    const name = getSecurityBusinessDisplayName(business);
    setSecurityConfirm({
      type: 'forceLogout',
      title: 'Force Logout',
      message: `Force logout ${name} from all active sessions?`,
      confirmLabel: 'Confirm Force Logout',
    });
  };

  const openPasswordResetConfirm = () => {
    const name = getSecurityBusinessDisplayName(business);
    setSecurityConfirm({
      type: 'passwordReset',
      title: 'Send Password Reset',
      message: `Send a password-reset link to the registered email for ${name}?`,
      confirmLabel: 'Send Reset Link',
    });
  };

  const handleSecurityConfirm = () => {
    if (!securityConfirm) return;
    if (securityConfirm.type === 'forceLogout') {
      setBusiness((prev) => ({
        ...prev,
        currentActiveSessions: 0,
        activeSessions: 0,
        auditLogs: appendSecurityAuditLog(
          prev,
          'Force Logout',
          'All active sessions terminated for this business account'
        ),
      }));
    } else if (securityConfirm.type === 'passwordReset') {
      setBusiness((prev) => ({
        ...prev,
        auditLogs: appendSecurityAuditLog(
          prev,
          'Send Password Reset',
          'Password-reset link sent to the registered business email'
        ),
      }));
    }
    setSecurityConfirm(null);
  };

  const getDetailTablePagination = (totalCount, currentPage) => {
    const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
    const effectivePage = Math.min(currentPage, totalPages);
    const startIndex = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
    return {
      totalPages,
      effectivePage,
      rangeStart: totalCount === 0 ? 0 : startIndex + 1,
      rangeEnd: Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount),
      showPagination: totalCount > CONFIG_LIST_PAGE_SIZE,
      paginate: (items) => items.slice(startIndex, startIndex + CONFIG_LIST_PAGE_SIZE),
    };
  };

  useEffect(() => {
    if (!business) return;
    const flagsCount = buildBusinessDetailFlagRows(business).length;
    setFlagsCurrentPage((p) =>
      Math.min(p, Math.max(1, Math.ceil(flagsCount / CONFIG_LIST_PAGE_SIZE)))
    );
  }, [business]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>Loading business details…</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  if (!business) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AdminAlertText className="text-alertred mb-4 block">Business not found</AdminAlertText>
            <AdminRowAction onClick={() => navigate('/admin/users/businesses')}>
              Back to Businesses
            </AdminRowAction>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const businessFlagRows = buildBusinessDetailFlagRows(business);
  const flagsTablePagination = getDetailTablePagination(businessFlagRows.length, flagsCurrentPage);
  const paginatedFlagRows = flagsTablePagination.paginate(businessFlagRows);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'flags', label: 'Flags' },
    { id: 'security', label: 'Security' },
    { id: 'audit-log', label: 'Audit Log' },
  ];

  const overviewBusinessId = formatBusinessIdDisplay(business);
  const overviewAccountStatus = formatAccountStatusOverview(business);
  const overviewVerificationStatus = formatVerificationStatusOverview(business);
  const overviewMemberSince = business.joined ? formatDate(business.joined) : '—';
  const overviewLastActive = formatDateTime(business.lastActive || business.lastLogin);
  const headerDisplayName = business.businessName || '—';

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>

        {/* Persistent Business Header — title only */}
        <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
          <div className="flex items-center gap-4">
            <AdminProfileAvatar
              src={business.profilePicture || Profile}
              alt={headerDisplayName}
              onError={(e) => {
                e.target.src = Profile;
              }}
            />
            <div className="flex-1 min-w-0">
              <AdminPanelTitle>{headerDisplayName}</AdminPanelTitle>
            </div>
          </div>
        </DashboardMainContainer>

        {/* Tabs */}
        <DashboardMainContainer className="!bg-white">
          <div className="border-b border-lightgray overflow-hidden">
            <div 
              className="flex space-x-8 overflow-x-auto" 
              style={{ 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {tabs.map((tab) => (
                <AdminTabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </AdminTabButton>
              ))}
            </div>
          </div>
          <style dangerouslySetInnerHTML={{
            __html: `
              .overflow-x-auto::-webkit-scrollbar {
                display: none;
              }
            `
          }} />

          {/* Tab Content */}
          <div
            className={
              activeTab === 'flags' || activeTab === 'audit-log'
                ? `${ADMIN_TAB_CONTENT_OFFSET_CLASS} w-full min-w-0`
                : ADMIN_TAB_CONTENT_CONTAINER_CLASS
            }
          >
            {activeTab === 'overview' && (
              <div className="space-y-6">
                  <div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                      {/* Column 1 — Business Details */}
                      <div className="space-y-4">
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Business ID</AdminDetailFieldLabel>
                          <div className="flex items-center gap-2 min-w-0">
                            <AdminDetailFieldValue as="div" className="truncate min-w-0">
                              {overviewBusinessId || '—'}
                            </AdminDetailFieldValue>
                            {overviewBusinessId ? (
                              <button
                                type="button"
                                onClick={handleCopyBusinessId}
                                className="inline-flex items-center justify-center shrink-0 text-mediumgray hover:text-primaryblue"
                                title={businessIdCopied ? 'Copied' : 'Copy Business ID'}
                                aria-label={businessIdCopied ? 'Copied' : 'Copy Business ID'}
                              >
                                <i
                                  className={`fi ${
                                    businessIdCopied ? 'fi-rr-check text-freshgreen' : 'fi-rr-copy'
                                  } text-xs leading-none`}
                                  aria-hidden
                                />
                              </button>
                            ) : null}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Account Status</AdminDetailFieldLabel>
                          <AdminStatusBadge
                            status={overviewAccountStatus}
                            label={overviewAccountStatus}
                          />
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Verification Status</AdminDetailFieldLabel>
                          <AdminDetailFieldValue
                            className={
                              overviewVerificationStatus === 'Verified'
                                ? 'text-freshgreen'
                                : 'text-warmyellow'
                            }
                          >
                            {overviewVerificationStatus}
                          </AdminDetailFieldValue>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Member Since</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>{overviewMemberSince}</AdminDetailFieldValue>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Last Active</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>{overviewLastActive}</AdminDetailFieldValue>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>ABN</AdminDetailFieldLabel>
                          <div className="flex items-center gap-2 min-w-0">
                            <AdminDetailFieldValue
                              as="span"
                              className="truncate min-w-0"
                              title={formatAbnDisplay(business)}
                            >
                              {formatAbnDisplay(business)}
                            </AdminDetailFieldValue>
                            {business.abn && business.abnVerified ? (
                              <i
                                className="fi fi-sr-check-circle text-freshgreen text-xs leading-none shrink-0"
                                title="Verified"
                                aria-label="Verified"
                              />
                            ) : null}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Trade Category</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>{getOverviewTradeCategory(business)}</AdminDetailFieldValue>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Business Email</AdminDetailFieldLabel>
                          {renderCopyableValue({
                            fieldKey: 'email',
                            displayValue: business.businessEmail || business.email || '—',
                            copyValue: business.businessEmail || business.email,
                            verified: Boolean(
                              (business.businessEmail || business.email) &&
                                business.emailVerified &&
                                !business.emailInvalid
                            ),
                          })}
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Mobile</AdminDetailFieldLabel>
                          {renderCopyableValue({
                            fieldKey: 'mobile',
                            displayValue: business.mobile || business.primaryContactNumber || '—',
                            copyValue: String(
                              business.mobile || business.primaryContactNumber || ''
                            ).replace(/\D/g, '') || business.mobile || business.primaryContactNumber,
                            verified: Boolean(
                              (business.mobile || business.primaryContactNumber) &&
                                business.phoneVerified
                            ),
                          })}
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Landline</AdminDetailFieldLabel>
                          {renderCopyableValue({
                            fieldKey: 'landline',
                            displayValue: business.landline || '—',
                            copyValue: business.landline,
                          })}
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Website</AdminDetailFieldLabel>
                          <AdminDetailFieldValue className="break-words">
                            {(() => {
                              const w = business.website || '';
                              if (!w || w === '—') return '—';
                              const url = w.startsWith('http') ? w : `https://${w.replace(/^\/\//, '')}`;
                              return (
                                <AdminLinkAction href={url} target="_blank" rel="noopener noreferrer">
                                  {w}
                                </AdminLinkAction>
                              );
                            })()}
                          </AdminDetailFieldValue>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Location</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>{getOverviewLocation(business)}</AdminDetailFieldValue>
                        </div>
                      </div>

                      {/* Column 2 — Plan & Billing */}
                      <div className="space-y-4">
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Current Plan</AdminDetailFieldLabel>
                          <div>
                            <AdminStatusBadge
                              status={formatPlanOverview(business)}
                              label={formatPlanOverview(business)}
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Subscription Status</AdminDetailFieldLabel>
                          <div>
                            {(() => {
                              const subscriptionStatus =
                                getBillingField(business, 'subscriptionStatus') !== '—'
                                  ? getBillingField(business, 'subscriptionStatus')
                                  : getBillingField(business, 'planStatus', 'Active');
                              return (
                                <AdminStatusBadge
                                  status={subscriptionStatus}
                                  label={subscriptionStatus}
                                />
                              );
                            })()}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Billing Cycle</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>
                            {getBillingField(business, 'billingCycle', business.planType || 'Monthly')}
                          </AdminDetailFieldValue>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Current Billing Period</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>{getCurrentBillingPeriod(business)}</AdminDetailFieldValue>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Last Payment Status</AdminDetailFieldLabel>
                          <div>
                            <AdminStatusBadge
                              status={getBillingField(business, 'paymentStatus', 'Succeeded')}
                              label={getBillingField(business, 'paymentStatus', 'Succeeded')}
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Next Billing Date</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>
                            {(() => {
                              const next =
                                business.nextBillingDate ||
                                business.subscription?.renewalDate ||
                                business.subscription?.nextBillingDate;
                              return next ? formatDate(next) : '—';
                            })()}
                          </AdminDetailFieldValue>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Grace Period End Date</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>
                            {getBillingField(
                              business,
                              'gracePeriodEndDate',
                              getBillingField(business, 'failedPaymentGracePeriod', 'Not applicable')
                            )}
                          </AdminDetailFieldValue>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Billing & Subscriptions</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>
                            <AdminLinkAction
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/admin/billing/subscriptions/${encodeURIComponent(overviewBusinessId)}`
                                )
                              }
                            >
                              Open Billing & Subscriptions
                            </AdminLinkAction>
                          </AdminDetailFieldValue>
                        </div>
                      </div>

                      {/* Column 3 — Activity + Compliance */}
                      <div className="space-y-4">
                        {(() => {
                          const postsCount = getOverviewPostsCount(business);
                          const featuredCount = getOverviewFeaturedPostsCount(business);
                          const reviewsCount = getOverviewReviewsCount(business);
                          const averageRating = getOverviewAverageRating(business);
                          const messagesCount = getOverviewMessagesCount(business);
                          const quotesCount = getOverviewQuotesCount(business);
                          const bid = overviewBusinessId;
                          return (
                            <>
                              <div className="space-y-1">
                                <AdminDetailFieldLabel>Posts</AdminDetailFieldLabel>
                                <div className="space-y-1">
                                  <AdminDetailFieldValue className="tabular-nums">
                                    {postsCount > 0 ? (
                                      <button
                                        type="button"
                                        className="bg-transparent border-0 p-0 cursor-pointer text-offblack hover:text-primaryblue"
                                        onClick={() =>
                                          navigate('/admin/posts/all', {
                                            state: { businessId: bid },
                                          })
                                        }
                                      >
                                        {postsCount} Posts
                                      </button>
                                    ) : (
                                      <span className="text-mediumgray">0 Posts</span>
                                    )}
                                  </AdminDetailFieldValue>
                                  <AdminDetailFieldValue className="tabular-nums">
                                    {featuredCount > 0 ? (
                                      <button
                                        type="button"
                                        className="bg-transparent border-0 p-0 cursor-pointer text-offblack hover:text-primaryblue"
                                        onClick={() =>
                                          navigate('/admin/posts/all', {
                                            state: { businessId: bid, featured: true },
                                          })
                                        }
                                      >
                                        {featuredCount} Featured
                                      </button>
                                    ) : (
                                      <span className="text-mediumgray">0 Featured</span>
                                    )}
                                  </AdminDetailFieldValue>
                                </div>
                              </div>
                              <div className="space-y-1">
                                <AdminDetailFieldLabel>Reviews</AdminDetailFieldLabel>
                                <div className="space-y-1">
                                  <AdminDetailFieldValue className="tabular-nums">
                                    {reviewsCount > 0 ? (
                                      <button
                                        type="button"
                                        className="bg-transparent border-0 p-0 cursor-pointer text-offblack hover:text-primaryblue"
                                        onClick={() =>
                                          navigate('/admin/reviews/all', {
                                            state: { businessId: bid },
                                          })
                                        }
                                      >
                                        {reviewsCount} Reviews
                                      </button>
                                    ) : (
                                      <span className="text-mediumgray">0 Reviews</span>
                                    )}
                                  </AdminDetailFieldValue>
                                  <AdminDetailFieldValue className="tabular-nums">
                                    <span className={averageRating != null ? 'text-offblack' : 'text-mediumgray'}>
                                      {averageRating != null
                                        ? `${averageRating} Average Rating`
                                        : '— Average Rating'}
                                    </span>
                                  </AdminDetailFieldValue>
                                </div>
                              </div>
                              {renderOverviewLinkedField({
                                label: 'Messages',
                                valueText: String(messagesCount),
                                enabled: messagesCount > 0,
                                onNavigate: () =>
                                  navigate('/admin/communication/all', {
                                    state: { businessId: bid },
                                  }),
                              })}
                              {renderOverviewLinkedField({
                                label: 'Quotes',
                                valueText: String(quotesCount),
                                enabled: quotesCount > 0,
                                onNavigate: () =>
                                  navigate('/admin/quotes/all', {
                                    state: { businessId: bid },
                                  }),
                              })}
                            </>
                          );
                        })()}
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Licences</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>
                            {formatComplianceNames(getApprovedComplianceNames(business.licenses))}
                          </AdminDetailFieldValue>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Badges</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>
                            {formatComplianceNames(getApprovedComplianceNames(business.badges))}
                          </AdminDetailFieldValue>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Trade Tickets</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>
                            {formatComplianceNames(getApprovedComplianceNames(business.tickets))}
                          </AdminDetailFieldValue>
                        </div>
                      </div>

                      {/* Column 4 — Account & Support + Security Snapshot */}
                      <div className="space-y-4">
                        {(() => {
                          const openFlags = countOpenFlagsForOverview(business);
                          const supportTickets = getOverviewSupportTickets(business);
                          const activeAppeals = getOverviewActiveAppeals(business);
                          const bid = overviewBusinessId;
                          return (
                            <>
                              {renderOverviewLinkedField({
                                label: 'Open Flags',
                                valueText: String(openFlags),
                                enabled: true,
                                onNavigate: () => setActiveTab('flags'),
                              })}
                              {renderOverviewLinkedField({
                                label: 'Open Support Tickets',
                                valueText: String(supportTickets),
                                enabled: supportTickets > 0,
                                onNavigate: () =>
                                  navigate('/admin/support/tickets', {
                                    state: { businessId: bid },
                                  }),
                              })}
                              {renderOverviewLinkedField({
                                label: 'Active Appeals',
                                valueText: String(activeAppeals),
                                enabled: activeAppeals > 0,
                                onNavigate: () =>
                                  navigate('/admin/appeals', {
                                    state: { businessId: bid },
                                  }),
                              })}
                            </>
                          );
                        })()}
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Last Login</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>
                            {formatDateTime(business.lastLogin || business.lastActive)}
                          </AdminDetailFieldValue>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Active Sessions</AdminDetailFieldLabel>
                          <AdminDetailFieldValue className="tabular-nums">
                            {business.currentActiveSessions ?? business.activeSessions ?? 0}
                          </AdminDetailFieldValue>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>
                            Failed Login Attempts
                            <br />
                            Last 24 Hours
                          </AdminDetailFieldLabel>
                          <AdminDetailFieldValue className="tabular-nums">
                            {business.failedLoginAttempts24h ?? 0}
                          </AdminDetailFieldValue>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            )}

            {activeTab === 'flags' && (
              <div className="min-w-0 max-w-full">
                <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                  <div className="w-full min-w-0 max-w-full">
                    <div
                      className="grid min-w-0 py-3 text-offblack border-b border-lightgray gap-x-2 sm:gap-x-3 gap-y-2 px-4"
                      style={{ gridTemplateColumns: FLAGS_TABLE_GRID }}
                    >
                      <AdminDetailTableHeader className="min-w-0 break-words">Flag ID</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">Case ID</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">Source</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">Reason</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">Severity</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">Case Status</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words leading-tight">Created On</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words leading-tight">Last Updated</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">Action</AdminDetailTableHeader>
                    </div>

                    {paginatedFlagRows.length > 0 ? (
                      paginatedFlagRows.map((flag) => (
                        <div
                          key={flag.key}
                          className="grid min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                          style={{ gridTemplateColumns: FLAGS_TABLE_GRID }}
                        >
                          <AdminTableBody className="min-w-0 break-words leading-snug">{flag.flagId}</AdminTableBody>
                          <AdminTableBody className="min-w-0 break-words break-all leading-snug">
                            {flag.handlingRecord || '—'}
                          </AdminTableBody>
                          <AdminTableBody className="min-w-0 break-words leading-snug">{flag.source || '—'}</AdminTableBody>
                          <AdminTableBody className="min-w-0 break-words break-all leading-snug" title={flag.reason || ''}>
                            {flag.reason || '—'}
                          </AdminTableBody>
                          <AdminTableBody
                            emphasis
                            className={`min-w-0 break-words leading-snug ${getFlagSeverityTextClass(
                              flag.severity
                            )}`.trim()}
                          >
                            {flag.severity || '—'}
                          </AdminTableBody>
                          <div className="flex items-start min-w-0">
                            <AdminFlagStatusBadge
                              status={flag.handlingStatus}
                              label={flag.handlingStatus || '—'}
                            />
                          </div>
                          <AdminTableBody className="min-w-0 break-words leading-snug">
                            {formatDateTime(flag.createdAt || flag.created)}
                          </AdminTableBody>
                          <AdminTableBody className="min-w-0 break-words leading-snug">
                            {formatDateTime(flag.updatedAt || flag.lastUpdated)}
                          </AdminTableBody>
                          <div className="flex items-start min-w-0">
                            {flag.actionLabel && flag.actionPath ? (
                              <button
                                type="button"
                                className="text-left bg-transparent border-0 p-0 cursor-pointer"
                                onClick={() => handleFlagRowAction(flag)}
                              >
                                <AdminTableBody className="text-offblack underline-offset-2 hover:underline">
                                  {flag.actionLabel}
                                </AdminTableBody>
                              </button>
                            ) : (
                              <AdminTableBody>—</AdminTableBody>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="py-8 text-center">
                        <AdminSupportingText className="text-mediumgray">No flags found</AdminSupportingText>
                      </div>
                    )}
                  </div>
                </DashboardMainContainer>
                <ConfigurationListPaginationFooter
                  show={flagsTablePagination.showPagination}
                  rangeStart={flagsTablePagination.rangeStart}
                  rangeEnd={flagsTablePagination.rangeEnd}
                  totalCount={businessFlagRows.length}
                  currentPage={flagsTablePagination.effectivePage}
                  totalPages={flagsTablePagination.totalPages}
                  onPrev={() => setFlagsCurrentPage((prev) => Math.max(1, prev - 1))}
                  onNext={() =>
                    setFlagsCurrentPage((prev) =>
                      Math.min(flagsTablePagination.totalPages, prev + 1)
                    )
                  }
                  goToPage={setFlagsCurrentPage}
                />
              </div>
            )}

            {activeTab === 'security' && (
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Column 1 — Login Activity */}
                <div className="space-y-4">
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Last Login</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>
                      {formatDateTime(business.lastLogin || business.lastActive)}
                    </AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>
                      Failed Login Attempts
                      <br />
                      Last 24 Hours
                    </AdminDetailFieldLabel>
                    <AdminDetailFieldValue className="tabular-nums text-offblack">
                      {business.failedLoginAttempts24h ?? 0}
                    </AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>
                      Failed Login Attempts
                      <br />
                      Last 7 Days
                    </AdminDetailFieldLabel>
                    <AdminDetailFieldValue className="tabular-nums text-offblack">
                      {business.failedLoginAttempts7Days ?? 0}
                    </AdminDetailFieldValue>
                  </div>
                </div>

                {/* Column 2 — Sessions */}
                <div className="space-y-4">
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Active Sessions</AdminDetailFieldLabel>
                    <AdminDetailFieldValue className="tabular-nums text-offblack">
                      {getSecurityActiveSessions(business)}
                    </AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Trusted Devices</AdminDetailFieldLabel>
                    <AdminDetailFieldValue className="tabular-nums text-offblack">
                      {business.trustedDevices ?? business.devices?.length ?? 0}
                    </AdminDetailFieldValue>
                  </div>
                </div>

                {/* Column 3 — Devices */}
                <div className="space-y-4">
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Last Device Used</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>
                      {business.lastDeviceUsed ||
                        (business.devices?.[0]?.device
                          ? `${business.devices[0].device}`
                          : null) ||
                        '—'}
                    </AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Last Device Added</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>
                      {business.lastDeviceAdded
                        ? formatDate(business.lastDeviceAdded)
                        : business.devices?.[0]?.date
                          ? formatDate(business.devices[0].date)
                          : '—'}
                    </AdminDetailFieldValue>
                  </div>
                </div>

                {/* Column 4 — Security Actions */}
                <div className="space-y-4">
                  <AdminHeaderAction
                    type="button"
                    disabled={getSecurityActiveSessions(business) <= 0}
                    onClick={openForceLogoutConfirm}
                  >
                    Force Logout
                  </AdminHeaderAction>
                  <AdminHeaderAction type="button" onClick={openPasswordResetConfirm}>
                    Send Password Reset
                  </AdminHeaderAction>
                </div>
              </div>
            )}

            {activeTab === 'audit-log' && (
              <div className="min-w-0 max-w-full">
                <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                  <div className="w-full min-w-0 max-w-full">
                    <div
                      className="grid min-w-0 py-3 text-offblack border-b border-lightgray gap-x-2 sm:gap-x-3 gap-y-2 px-4"
                      style={{ gridTemplateColumns: AUDIT_LOG_TABLE_GRID }}
                    >
                      <AdminDetailTableHeader className="min-w-0 break-words">
                        Date and Time
                      </AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">
                        Event
                      </AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">
                        Details
                      </AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">
                        Related Record
                      </AdminDetailTableHeader>
                    </div>

                    {(business.auditLogs || []).length > 0 ? (
                      (business.auditLogs || []).map((log, index) => {
                        const details =
                          log.details ||
                          [log.performedBy || log.userType, log.summary]
                            .filter(Boolean)
                            .join(' · ');
                        const related =
                          log.relatedRecord ||
                          log.ref?.id ||
                          log.caseId ||
                          log.flagId ||
                          '—';
                        const rawWhen = log.dateTime || log.timestamp;
                        const when =
                          rawWhen && String(rawWhen).includes('T')
                            ? formatDateTime(rawWhen)
                            : rawWhen || '—';
                        return (
                          <div
                            key={log.id || `audit-${index}`}
                            className="grid min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                            style={{ gridTemplateColumns: AUDIT_LOG_TABLE_GRID }}
                          >
                            <AdminTableBody className="min-w-0 break-words leading-snug">
                              {when}
                            </AdminTableBody>
                            <AdminTableBody className="min-w-0 break-words leading-snug">
                              {log.action || log.event || '—'}
                            </AdminTableBody>
                            <AdminTableBody
                              className="min-w-0 break-words break-all leading-snug"
                              title={details || '—'}
                            >
                              {details || '—'}
                            </AdminTableBody>
                            <AdminTableBody className="min-w-0 break-words leading-snug">
                              {related}
                            </AdminTableBody>
                          </div>
                        );
                      })
                    ) : (
                      <div className="py-8 text-center">
                        <AdminSupportingText className="text-mediumgray">
                          No audit log entries
                        </AdminSupportingText>
                      </div>
                    )}
                  </div>
                </DashboardMainContainer>
              </div>
            )}
          </div>
        </DashboardMainContainer>
        </div>
      </div>
      {securityConfirm ? (
        <BusinessSecurityConfirmModal
          title={securityConfirm.title}
          message={securityConfirm.message}
          confirmLabel={securityConfirm.confirmLabel}
          onConfirm={handleSecurityConfirm}
          onClose={() => setSecurityConfirm(null)}
        />
      ) : null}
    </AdminLayout>
  );
};

export default BusinessDetail;
